<template>
  <div class="container-fluid">
    <div class="card mb-25 border-0 rounded-0 bg-white shadow-sm">
      <!-- Header bleu -->
      <div class="card-header d-flex justify-content-between align-items-center py-4 px-4"
        style="background-color: #0a59a4;">
        <div>
          <h3 class="text-white fw-bold mb-1">
            Détail du BESC – {{ besc.refbesc || 'Chargement...' }}
          </h3>
          <small class="text-white opacity-75">
            Référence : {{ besc.refbesc }} . Créé le {{ formatDate(besc.createdAt) }}
          </small>
        </div>
        <div>
          <!-- Badge statut -->
          <span class="badge fs-5 px-4 py-2" :class="statusBadgeClass">
            {{ statusLabel }}
          </span>
        </div>
      </div>

      <div class="card-body p-15 p-sm-20 p-md-30 letter-spacing">
        <div v-if="totalCriticalFlags > 0" class="alert alert-danger border-0 shadow-sm mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <i class="fas fa-ban fa-2x text-danger"></i>
              <div>
                <h5 class="mb-1 fw-bold text-danger">
                  ÉMISSION BLOQUÉE
                  <span class="ms-2">
                    {{ totalCriticalFlags }} anomalie(s) critique(s)
                  </span>
                </h5>
                <p class="mb-0">
                  <span v-if="totalBlockingFlags > 0">
                    {{ totalBlockingFlags }} bloquante(s)
                  </span>
                  <span v-if="totalBlockingFlags > 0 && totalHighRiskFlags > 0"> </span>
                  <span v-if="totalHighRiskFlags > 0">
                    {{ totalHighRiskFlags }} risque(s) élevé(s)
                  </span>
                  Ce BESC ne peut pas être émis.
                </p>
              </div>
            </div>
            <button @click="showFlagsModal = true" class="btn btn-danger">
              <i class="flaticon-exclamation-triangle me-2"></i>
              Voir les {{ totalCriticalFlags }} alerte(s)
            </button>
          </div>
        </div>

        <div v-else-if="totalWarningFlags > 0" class="alert alert-warning border-0 shadow-sm mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <i class="fas fa-info-circle fa-2x text-warning"></i>
              <div>
                <h6 class="mb-1 fw-bold">
                  Avertissements ({{ totalWarningFlags }})
                </h6>
                <p class="mb-0">
                  Points d'attention détectés — N'empêchent pas l'émission
                </p>
              </div>
            </div>
            <button @click="showFlagsModal = true" class="btn btn-warning">
              Consulter
            </button>
          </div>
        </div>

        <div v-else-if="totalInfoFlags > 0" class="alert alert-info border-0 shadow-sm mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <i class="fas fa-lightbulb fa-2x text-info"></i>
              <div>
                <h6 class="mb-1 fw-bold"> Informations ({{ totalInfoFlags }})</h6>
                <p class="mb-0">Éléments à noter pour ce dossier</p>
              </div>
            </div>
            <button @click="showFlagsModal = true" class="btn btn-info">Voir</button>
          </div>
        </div>

        <div v-else-if="!loading && totalAllFlags === 0" class="alert alert-success border-0 shadow-sm mb-4">
          <div class="d-flex align-items-center gap-3">
            <i class="fas fa-check-circle fa-2x text-success"></i>
            <div>
              <h6 class="mb-1 fw-bold text-success"> Aucune alerte détectée</h6>
              <p class="mb-0">Tous les contrôles sont passés avec succès.</p>
            </div>
          </div>
        </div>

        <!-- ====================== RETOURS DE CORRECTIONS ====================== -->
        <div v-if="correctionRetourne.length > 0" class="border-0 shadow-sm mb-4">
          <div class="d-flex justify-content-between align-items-center">
            <div class="flex-grow-1">
              <div class="d-flex align-items-center gap-3 mb-3">
                <i class="fas fa-reply fa-2x text-warning"></i>
                <div>
                  <h5 class="mb-1 fw-bold text-primary">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    Corrections demandées ({{ correctionRetourne.length }})
                  </h5>
                  <p class="mb-0">
                    Des corrections ont été demandées par l'agent validateur
                  </p>
                </div>
              </div>

              <!-- Liste des corrections -->
              <div class="corrections-list">
                <div v-for="(correction, index) in correctionRetourne" :key="correction.id"
                  class="card mb-2 border-warning">
                  <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <div class="flex-grow-1">
                        <div class="d-flex align-items-center gap-2 mb-2">
                          <span class="badge bg-warning text-dark">
                            Correction {{ index + 1 }}
                          </span>
                          <span class="badge" :class="{
                            'bg-danger': correction.status === 'OPEN',
                            'bg-success': correction.status === 'RESOLVED',
                            'bg-secondary': correction.status === 'CANCELLED'
                          }">
                            {{ correction.status === 'OPEN' ? 'En attente' :
                              correction.status === 'RESOLVED' ? 'Résolu' : 'Annulé' }}
                          </span>
                        </div>

                        <div class="mb-2">
                          <strong class="text-primary">
                            <i class="fas fa-edit me-1"></i>
                            Champs concernés :
                          </strong>
                          <p class="mb-0 ms-3">{{ correction.fields_concerned }}</p>
                        </div>

                        <div class="mb-2">
                          <strong class="text-danger">
                            <i class="fas fa-comment-dots me-1"></i>
                            Message de l'agent :
                          </strong>
                          <p class="mb-0 ms-3 fst-italic">{{ correction.message_to_requester }}</p>
                        </div>

                        <div class="row text-muted small mt-2">
                          <div class="col-md-4">
                            <i class="fas fa-calendar me-1"></i>
                            <strong>Demandé le :</strong>
                            {{ formatDateTime(correction.requested_at) }}
                          </div>
                          <div class="col-md-4">
                            <i class="fas fa-clock me-1"></i>
                            <strong>Échéance :</strong>
                            {{ formatDate(correction.due_date) }}
                          </div>
                          <div class="col-md-4" v-if="correction.responded_at">
                            <i class="fas fa-check me-1 text-success"></i>
                            <strong>Répondu le :</strong>
                            {{ formatDateTime(correction.responded_at) }}
                          </div>
                        </div>

                        <!-- Réponse du transitaire si elle existe -->
                        <div v-if="correction.response_message" class="mt-3 p-2 bg-light rounded">
                          <strong class="text-success">
                            <i class="fas fa-reply me-1"></i>
                            Votre réponse :
                          </strong>
                          <p class="mb-0 ms-3">{{ correction.response_message }}</p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isReverificationNeeded" class="alert border-start border-info border-4 mb-4">
          <div class="d-flex align-items-center gap-3">
            <i class="fas fa-redo fa-2x text-info"></i>
            <div>
              <h6 class="mb-1 fw-bold">Re-vérification requise</h6>
              <p class="mb-0">Ce dossier a été corrigé par le demandeur. Une nouvelle vérification complète est
                nécessaire.</p>
            </div>
          </div>
        </div>

        <div v-if="isDossierValidated && !isAlreadyValidatedByAgent"
          class="alert alert-success border-0 shadow-sm mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <i class="fas fa-check-circle fa-2x text-success"></i>
              <div>
                <h5 class="mb-1 fw-bold text-success">
                  <i class="flaticon-check-mark me-2"></i>
                  DOSSIER PRÊT POUR DÉCISION
                </h5>
                <p class="mb-0">
                  Tous les contrôles sont terminés ({{ getTotalChecksCount() }} checks complétés).
                  Aucune alerte critique. Vous pouvez valider ou rejeter le dossier.
                </p>
              </div>
            </div>
            <button @click="showValidationModal = true" class="btn btn-primary">
              <i class="fas fa-clipboard-check me-2"></i>
              PRENDRE UNE DÉCISION
            </button>
          </div>
        </div>

        <!-- ====================== INDICATEUR SI DÉJÀ VALIDÉ ====================== -->
        <div v-else-if="isAlreadyValidatedByAgent" class="alert border-0 shadow-sm mb-4" :class="{
          'alert-success': getAgentValidationStatus() === 'VALIDATED',
          'alert-danger': getAgentValidationStatus() === 'REJECTED'
        }">
          <div class="d-flex align-items-center gap-3">
            <i class="fa-2x" :class="{
              'fas fa-check-circle text-success': getAgentValidationStatus() === 'VALIDATED',
              'fas fa-times-circle text-danger': getAgentValidationStatus() === 'REJECTED'
            }"></i>
            <div>
              <h5 class="mb-1 fw-bold" :class="{
                'text-success': getAgentValidationStatus() === 'VALIDATED',
                'text-danger': getAgentValidationStatus() === 'REJECTED'
              }">
                <span v-if="getAgentValidationStatus() === 'VALIDATED'">
                  DOSSIER VALIDÉ PAR L'AGENT
                </span>
                <span v-else>
                  DOSSIER REJETÉ PAR L'AGENT
                </span>
              </h5>
              <p class="mb-0">
                {{ getAgentValidationStatus() === 'VALIDATED' ? 'Validé' : 'Rejeté' }} le {{
                  formatDateTime(getAgentValidationDate()) }}
                <br>
                <small class="text-muted">
                  <span v-if="getAgentValidationStatus() === 'VALIDATED'">
                    En attente d'approbation superviseur
                  </span>
                  <span v-else>
                    Dossier remis en correction
                  </span>
                </small>
              </p>
            </div>
          </div>
        </div>

        <!-- ====================== INFORMATIONS GÉNÉRALES ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Informations générales</h5>
        </div>
        <div class="row g-4 mb-5">
          <div class="col-md-4">
            <label class="text-black fw-semibold">Numéro de connaissement</label>
            <p class="mb-0">{{ besc.numeroConnaissement || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Pays d'origine</label>
            <p class="mb-0">{{ besc.paysOrigine?.name_fr || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Incoterm</label>
            <p class="mb-0">{{ besc.incoterm?.code || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Type de paiement fret</label>
            <p class="mb-0">{{ besc.typeFret?.libelle || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Destination finale</label>
            <p class="mb-0">{{ besc.destinationFinalePays?.name_fr || '-' }}</p>
          </div>
        </div>

        <!-- ====================== PARTIES PRENANTES ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Parties prenantes</h5>
        </div>
        <div class="row g-4 mb-5">
          <div class="col-md-3" v-if="importateur">
            <label class="text-black fw-semibold">Expéditeur</label>
            <p class="mb-0">{{ importateur.acteur.nomComplet || '-' }}</p>
          </div>
          <div class="col-md-3" v-if="importateur">
            <label class="text-black fw-semibold">Importateur</label>
            <p class="mb-0">{{ importateur.acteur.nomComplet || '-' }}</p>
          </div>
          <div class="col-md-3" v-if="transitaire">
            <label class="text-black fw-semibold">Transitaire</label>
            <p class="mb-0">{{ transitaire.acteur.nomComplet || '-' }}</p>
          </div>
          <div class="col-md-3" v-if="partieANotifier">
            <label class="text-black fw-semibold">Partie à notifier</label>
            <p class="mb-0">{{ partieANotifier.acteur.nomComplet || '-' }}</p>
          </div>
        </div>

        <!-- ====================== TRANSPORT & ITINÉRAIRE ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Transport & itinéraire</h5>
        </div>
        <div class="row g-4 mb-5">
          <div class="col-md-4" v-if="transporteur">
            <label class="text-black fw-semibold">Transporteur</label>
            <p class="mb-0">{{ transporteur.acteur.nomComplet || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Navire</label>
            <p class="mb-0">{{ besc.vessel?.name || '-' }} <small v-if="besc.vessel?.imo" class="text-muted">(IMO: {{
              besc.vessel.imo }})</small></p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Port de chargement</label>
            <p class="mb-0">{{ besc.portChargement?.nomPort || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Port de déchargement</label>
            <p class="mb-0">{{ besc.portDechargement?.nomPort || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">ETD (Départ estimé)</label>
            <p class="mb-0">{{ formatDate(besc.etd) }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">ETA (Arrivée estimée)</label>
            <p class="mb-0">{{ formatDate(besc.eta) }}</p>
          </div>

          <!-- Transbordement -->
          <div class="col-12" v-if="besc.transbordement === 'oui'">
            <div class="alert alert-info" v-if="transbordement">
              <strong>Transbordement :</strong> Oui<br>
              Navire après transbordement : {{ besc.navireApresTransbordement?.name || '-' }}<br>
              Transporteur après transbordement : {{ transbordement.acteur.nomComplet || '-' || '-' }}
            </div>
          </div>
        </div>

        <!-- ====================== COMMENTAIRES GLOBAUX ====================== -->
        <div class="mb-4 mt-5">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="text-primary fw-bold border-bottom pb-2 mb-0">
              Commentaires globaux ({{ globalComments.length }})
            </h5>
            <button @click="openGlobalCommentModal" class="btn btn-sm btn-primary">
              <i class="fas fa-plus me-2"></i>
              Ajouter un commentaire
            </button>
          </div>
        </div>

        <div v-if="globalComments.length" class="mb-5">
          <div v-for="comment in globalComments" :key="comment.id" class="card mb-3 shadow-sm"
            :class="comment.visibility === 'EXTERNAL' ? 'border-warning' : 'border-secondary'">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <span class="badge me-2"
                    :class="comment.visibility === 'EXTERNAL' ? 'bg-warning text-dark' : 'bg-secondary'">
                    <i :class="comment.visibility === 'EXTERNAL' ? 'fas fa-eye' : 'fas fa-lock'"></i>
                    {{ comment.visibility === 'EXTERNAL' ? 'Externe' : 'Interne' }}
                  </span>
                  <strong>{{ comment.author?.username || 'Utilisateur' }}</strong>
                  <small class="text-muted ms-2">
                    {{ formatDateTime(comment.createdAt) }}
                  </small>
                </div>
                <div class="btn-group btn-group-sm">
                  <button @click="editComment(comment)" class="btn btn-outline-primary">
                    <i class="flaticon-edit"></i>
                  </button>
                  <button @click="deleteComment(comment.id)" class="btn btn-outline-danger">
                    <i class="flaticon-delete"></i>
                  </button>
                </div>
              </div>
              <p class="mb-0">{{ comment.commentText }}</p>
            </div>
          </div>
        </div>

        <div v-else class="alert alert-light text-center mb-5">
          <i class="fas fa-comment-slash fa-2x text-muted mb-2"></i>
          <p class="mb-0 text-muted">Aucun commentaire global pour ce dossier</p>
        </div>
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">
            Marchandises ({{ totalMarchandises }})
          </h5>
        </div>

        <div v-if="besc.bls?.length" class="mb-5">
          <div class="accordion" id="blAccordion">
            <div v-for="(bl, blIndex) in besc.bls" :key="blIndex" class="accordion-item border mb-3 shadow-sm">
              <!-- Header de l'accordéon -->
              <h2 class="accordion-header">
                <button class="accordion-button collapsed bg-blue-header text-white fw-bold" type="button"
                  :data-bs-toggle="'collapse'" :data-bs-target="'#bl' + blIndex"
                  :aria-expanded="blIndex === 0 ? 'true' : 'false'" :aria-controls="'bl' + blIndex">
                  <i class="fas fa-file-alt me-3"></i>
                  BL N° {{ bl.blNumber }}
                  <span class="badge bg-white text-primary ms-3">
                    {{ bl.bescLignes?.length || 0 }} marchandise(s)
                  </span>
                  <span v-if="bl.conteneurs?.length" class="badge bg-white text-primary ms-2">
                    {{ bl.conteneurs.length }} conteneur(s)
                  </span>
                </button>
              </h2>

              <div :id="'bl' + blIndex" class="accordion-collapse collapse" :class="{ show: blIndex === 0 }"
                data-bs-parent="#blAccordion">
                <div class="accordion-body p-0">

                  <!-- Table des marchandises -->
                  <div v-if="bl.bescLignes?.length" class="p-3">
                    <div class="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                      <h6 class="text-primary fw-bold mb-0">
                        <i class="fas fa-boxes me-2"></i>Détail des marchandises
                      </h6>
                      <div class="dropdown">
                        <button class="btn btn-sm btn-outline-primary" @click="openCommentModal(bl, Number(blIndex))">
                          <i class="fas fa-comment-dots me-1"></i>
                          Ajouter un commentaire
                        </button>
                        <!-- <ul class="dropdown-menu" :aria-labelledby="'dropdownBL' + blIndex">
                          <li>
                            <a class="dropdown-item" @click.prevent="openCommentModal(bl, Number(blIndex))">
                              <i class="fas fa-comment me-2 text-primary"></i>
                              Commentaire général sur ce BL
                            </a>
                          </li>
                          <li v-if="validationTasks.length">
                            <hr class="dropdown-divider">
                          </li>
                          <li v-if="validationTasks.length" class="dropdown-header">
                            <small class="text-muted">Lier à une tâche de validation</small>
                          </li>
                          <li v-for="task in validationTasks" :key="task.id">
                            <a class="dropdown-item"
                              @click.prevent="openCommentModalWithTask('BILL_OF_LADING', bl.id, task.id)">
                              <i class="fas fa-tasks me-2 text-info"></i>
                              <span>{{ task.task_name }}</span>
                              <span class="badge ms-2" :class="{
                                'bg-success': task.status === 'COMPLETED',
                                'bg-warning text-dark': task.status === 'IN_PROGRESS',
                                'bg-secondary': task.status === 'PENDING'
                              }">
                                {{ translateStatus(task.status) }}
                              </span>

                            </a>
                          </li>
                          <li v-if="!validationTasks.length">
                            <span class="dropdown-item text-muted small">
                              <i class="fas fa-info-circle me-2"></i>
                              Aucune tâche de validation disponible
                            </span>
                          </li>
                        </ul> -->
                      </div>

                    </div>

                    <div v-if="getBlComments(bl.id).length" class="mb-3">
                      <h6 class="text-muted small mb-2">
                        <i class="fas fa-comments me-1"></i>
                        Commentaires sur ce BL
                      </h6>

                      <div v-for="comment in getBlComments(bl.id)" :key="comment.id" class="alert mb-2"
                        :class="comment.visibility === 'EXTERNAL' ? 'alert-warning' : 'alert-secondary'">
                        <div class="d-flex justify-content-between align-items-start">
                          <div class="flex-grow-1">
                            <div class="d-flex align-items-center mb-1">
                              <span class="badge me-2"
                                :class="comment.visibility === 'EXTERNAL' ? 'bg-warning text-dark' : 'bg-secondary'">
                                {{ comment.visibility === 'EXTERNAL' ? 'Externe' : 'Interne' }}
                              </span>

                              <!-- Badge si lié à une tâche -->
                              <span v-if="comment.validationTaskId" class="badge bg-info text-white me-2">
                                <i class="fas fa-tasks me-1"></i>
                                Tâche de validation
                              </span>

                              <strong class="me-2">{{ comment.author?.username }}</strong>

                              <small class="text-muted">{{ formatDateTime(comment.createdAt) }}</small>
                            </div>
                            <p class="mb-0">{{ comment.commentText }}</p>

                            <small v-if="comment.validationTask" class="text-muted d-block mt-1">
                              <i class="fas fa-link me-1"></i>
                              Lié à : {{ comment.validationTask.task_name }}
                            </small>
                          </div>
                          <div class="btn-group btn-group-sm ms-2">
                            <button @click="editComment(comment)" class="btn btn-sm btn-outline-primary">
                              <i class="flaticon-edit"></i>
                            </button>
                            <button @click="deleteComment(comment.id)" class="btn btn-sm btn-outline-danger">
                              <i class="flaticon-delete"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Affichage du commentaire existant -->
                    <div v-if="bl.comment" class="alert alert-warning mb-3">
                      <div class="d-flex align-items-start">
                        <i class="fas fa-comment-alt fa-lg me-2 mt-1"></i>
                        <div class="flex-grow-1">
                          <strong>Commentaire du contrôleur :</strong>
                          <p class="mb-0 mt-1">{{ bl.comment }}</p>
                        </div>
                      </div>
                    </div>

                    <div class="table-responsive">
                      <table class="table table-hover align-middle mb-0">
                        <thead style="background-color: #0a59a4;" class="text-white">
                          <tr>
                            <th>Description</th>
                            <th class="text-center">Qté</th>
                            <th class="text-center">Nb Colis</th>
                            <th class="text-end">Val. comm.</th>
                            <th class="text-end">Val. déclarée</th>
                            <th class="text-end">Poids brut</th>
                            <th class="text-end">Poids net</th>
                            <th class="text-end">Volume</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="ligne in bl.bescLignes" :key="ligne.id">
                            <td>{{ ligne.descriptionMarchandise || '-' }}</td>
                            <td class="text-center">{{ ligne.quantite || '-' }}</td>
                            <td class="text-center">{{ ligne.nombreColis || '-' }}</td>
                            <td class="text-end">{{ formatNumber(ligne.valeurCommerciale) }} {{ ligne.devise || 'USD' }}
                            </td>
                            <td class="text-end">{{ formatNumber(ligne.valeurDeclaree) }} {{ ligne.devise || 'USD' }}
                            </td>
                            <td class="text-end">{{ formatNumber(ligne.poidsBrut) }} kg</td>
                            <td class="text-end">{{ formatNumber(ligne.poidsNet) }} kg</td>
                            <td class="text-end">{{ formatNumber(ligne.volume) }} m³</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <!-- Bouton pour voir les conteneurs -->
                  <div v-if="bl.conteneurs?.length" class="p-3 bg-light border-top text-center">
                    <button @click="showConteneurs(bl.conteneurs, bl.blNumber)" class="btn btn-primary btn-lg">
                      <i class="fas fa-box me-2"></i>
                      Voir les {{ bl.conteneurs.length }} conteneur(s)
                    </button>
                  </div>
                  <!-- Message si vide -->
                  <div v-if="!bl.bescLignes?.length && !bl.conteneurs?.length" class="text-center text-muted py-4">
                    <i class="fas fa-inbox fa-2x mb-2 opacity-50"></i>
                    <p class="mb-0">Aucune marchandise ni conteneur pour ce BL.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-muted py-4">
          <i class="fas fa-inbox fa-3x mb-3 opacity-50"></i>
          <p class="mb-0">Aucun BL enregistré.</p>
        </div>

        <div v-if="showConteneursModal" class="modal-backdrop fade show" @click="showConteneursModal = false"></div>

        <div v-if="showConteneursModal" class="modal fade show d-block" tabindex="-1">
          <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">

              <div class="modal-header" style="background-color: #0a59a4;">
                <h5 class="modal-title text-white">
                  <i class="fas fa-box me-2"></i>
                  Conteneurs du BL N° {{ selectedBlNumber }}
                </h5>
                <button type="button" class="btn-close btn-close-white" @click="showConteneursModal = false"></button>
              </div>
              <div class="modal-body p-4">
                <div class="row g-3">
                  <div v-for="(conteneur, index) in selectedConteneurs" :key="conteneur.id" class="col-md-6">
                    <div class="card border-primary h-100 shadow-sm">
                      <div class="card-header bg-primary text-white">
                        <i class="fas fa-cube me-2"></i>
                        Conteneur {{ index + 1 }}
                      </div>
                      <div class="card-body">
                        <div class="mb-3 pb-3 border-bottom">
                          <label class="text-muted small fw-semibold d-block mb-1">
                            <i class="fas fa-barcode me-1"></i>Numéro de conteneur
                          </label>
                          <p class="mb-0 fw-bold text-primary fs-5">
                            {{ conteneur.numeroConteneur || 'Non renseigné' }}
                          </p>
                        </div>

                        <div class="row g-3">
                          <div class="col-4">
                            <label class="text-muted small fw-semibold d-block mb-1">
                              <i class="fas fa-lock me-1"></i>N° Scellé
                            </label>
                            <p v-if="conteneur.numeroScelle" class="mb-0 text-dark fw-bold">
                              {{ conteneur.numeroScelle }}
                            </p>
                            <p v-else class="mb-0 text-muted small fst-italic">
                              Non renseigné
                            </p>
                          </div>

                          <div class="col-4">
                            <label class="text-muted small fw-semibold d-block mb-1">
                              <i class="fas fa-weight-hanging me-1"></i>Poids brut
                            </label>
                            <p v-if="conteneur.poidsBrut" class="mb-0 text-dark fw-bold">
                              {{ formatNumber(conteneur.poidsBrut) }} kg
                            </p>
                            <p v-else class="mb-0 text-muted small fst-italic">
                              Non renseigné
                            </p>
                          </div>

                          <div class="col-4">
                            <label class="text-muted small fw-semibold d-block mb-1">
                              <i class="fas fa-cube me-1"></i>Volume
                            </label>
                            <p v-if="conteneur.volume" class="mb-0 text-dark fw-bold">
                              {{ formatNumber(conteneur.volume) }} m³
                            </p>
                            <p v-else class="mb-0 text-muted small fst-italic">
                              Non renseigné
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-footer bg-light">
                <button type="button" class="btn btn-secondary" @click="showConteneursModal = false">
                  <i class="fas fa-times me-2"></i>
                  Fermer
                </button>
              </div>

            </div>
          </div>
        </div>

        <div v-if="showCommentFormModal" class="modal fade show d-block" tabindex="-1">
          <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">

              <div class="modal-header" style="background-color: #0a59a4;">
                <h5 class="modal-title text-white">
                  <i class="fas fa-comment-dots me-2"></i>
                  {{ editingCommentId ? 'Modifier le commentaire' : 'Nouveau commentaire' }}
                </h5>
                <button type="button" class="btn-close btn-close-white" @click="closeCommentFormModal"></button>
              </div>

              <div class="modal-body p-4">
                <div class="alert alert-info mb-4">
                  <i class="fas fa-info-circle me-2"></i>
                  <strong>Contexte :</strong>
                  <span v-if="commentForm.related_entity_type === 'BESC_FILE'">
                    Commentaire global sur le dossier BESC
                  </span>
                  <span v-else-if="commentForm.related_entity_type === 'BILL_OF_LADING'">
                    Commentaire sur le BL N° {{ selectedBl?.blNumber }}
                  </span>
                  <span v-else-if="commentForm.related_entity_type === 'SUPPORTING_DOCUMENT'">
                    Commentaire sur un document justificatif
                  </span>
                  <span v-if="commentForm.validation_task_id">
                    Lié à une tâche de validation
                  </span>
                </div>

                <div class="form-group mb-3" v-if="validationTasks.length">
                  <label class="form-label fw-semibold">
                    <i class="fas fa-tasks me-2"></i>Tâche de validation associée (optionnel)
                  </label>
                  <Multiselect v-model="commentForm.validation_task_id" :options="taskOptions" label="label"
                    track-by="value" value-prop="value" placeholder="Aucune tâche (commentaire général)"
                    :searchable="true" :allow-empty="true" :close-on-select="true" />
                  <small class="text-muted">
                    Si ce commentaire concerne une tâche de validation spécifique, sélectionnez-la ici
                  </small>
                </div>

                <div class="form-group mb-3">
                  <label class="form-label fw-semibold">
                    <i class="fas fa-eye me-2"></i>Visibilité
                  </label>
                  <Multiselect v-model="commentForm.visibility" :options="visibiliteOptions" label="label"
                    track-by="value" value-prop="value" placeholder="Sélectionner la visibilité" :searchable="false"
                    :allow-empty="false" />
                  <small class="text-muted">
                    <span v-if="commentForm.visibility === 'INTERNAL'">
                      Ce commentaire ne sera visible que par les agents
                    </span>
                    <span v-else>
                      Ce commentaire sera visible par le transitaire/chargeur
                    </span>
                  </small>
                </div>

                <div class="form-group">
                  <label class="form-label fw-semibold">Commentaire *</label>
                  <textarea v-model="commentForm.comment_text" class="form-control" rows="6"
                    placeholder="Saisissez votre commentaire..."></textarea>
                  <small class="text-muted">
                    {{ commentForm.comment_text.length }} caractères
                  </small>
                </div>
              </div>

              <div class="modal-footer bg-light">
                <button type="button" class="btn btn-secondary" @click="closeCommentFormModal">
                  <i class="fas fa-times me-2"></i>
                  Annuler
                </button>
                <button type="button" class="btn btn-primary" @click="saveComment">
                  <i class="fas fa-save me-2"></i>
                  {{ editingCommentId ? 'Mettre à jour' : 'Enregistrer' }}
                </button>
              </div>

            </div>
          </div>
        </div>

        <!-- ===== DOCUMENTS JOINTS ===== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">
            Documents joints ({{ besc.documents?.length || 0 }})
          </h5>
        </div>

        <div class="row g-3" v-if="besc.documents?.length">
          <div class="col-md-4" v-for="doc in besc.documents" :key="doc.id">
            <div class="border rounded p-3 d-flex align-items-center justify-content-between">
              <div>
                <strong>{{ doc.typeDocument?.libelle || 'Document' }}</strong><br>
                <small class="text-muted">{{ doc.filename }}</small>
              </div>
              <div class="d-flex gap-1">
                <!-- VOIR EN MODAL au lieu de nouvel onglet -->
                <button class="btn btn-sm btn-primary" @click="openDocumentModal(bescId, doc.id, doc.filename)">
                  <i class="fas fa-eye me-1"></i>Voir
                </button>
                <button @click="openDocumentCommentModal(doc.id)" class="btn btn-sm btn-outline-secondary">
                  <i class="flaticon-comment lh-1 position-relative top-1"></i>
                </button>
              </div>
            </div>

            <div v-if="getDocumentComments(doc.id).length" class="mt-2">
              <small class="text-muted d-block mb-1">
                <i class="fas fa-comments me-1"></i>
                {{ getDocumentComments(doc.id).length }} commentaire(s)
              </small>
              <div v-for="comment in getDocumentComments(doc.id)" :key="comment.id" class="small p-2 rounded mb-1"
                :class="comment.visibility === 'EXTERNAL' ? 'bg-warning bg-opacity-10' : 'bg-light'">
                <div class="d-flex justify-content-between">
                  <strong>{{ comment.author?.username }}</strong>
                  <div>
                    <button @click="editComment(comment)" class="btn btn-sm btn-link p-0 me-1">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button @click="deleteComment(comment.id)" class="btn btn-sm btn-link p-0 text-danger">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <p class="mb-0 mt-1">{{ comment.commentText }}</p>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center text-muted py-4">Aucun document joint.</div>

        <!-- ===== MODAL VISUALISATION DOCUMENT ===== -->
        <div v-if="showDocumentModal" class="modal-backdrop fade show" @click="closeDocumentModal"></div>
        <div v-if="showDocumentModal" class="modal fade show d-block" tabindex="-1">
          <div class="modal-dialog modal-xl modal-dialog-centered" style="max-width: 90vw;">
            <div class="modal-content" style="height: 90vh;">

              <div class="modal-header" style="background-color: #0a59a4;">
                <h5 class="modal-title text-white">
                  <i class="fas fa-file me-2"></i>
                  {{ currentDocumentName }}
                </h5>
                <button type="button" class="btn-close btn-close-white" @click="closeDocumentModal"></button>
              </div>

              <div class="modal-body p-0 d-flex align-items-center justify-content-center bg-dark"
                style="flex: 1; overflow: hidden;">
                <!-- Loader pendant chargement -->
                <div v-if="loadingDocument" class="text-center text-white">
                  <div class="spinner-border text-light mb-3" role="status"></div>
                  <p>Chargement du document...</p>
                </div>

                <!-- Affichage PDF -->
                <iframe v-else-if="currentDocumentUrl && currentDocumentType === 'pdf'" :src="currentDocumentUrl"
                  style="width: 100%; height: 100%; border: none;"></iframe>

                <!-- Affichage image -->
                <img v-else-if="currentDocumentUrl && currentDocumentType === 'image'" :src="currentDocumentUrl"
                  style="max-width: 100%; max-height: 100%; object-fit: contain;" />

                <!-- Fallback -->
                <div v-else class="text-center text-white p-4">
                  <i class="fas fa-file fa-4x mb-3 opacity-50"></i>
                  <p>Ce type de fichier ne peut pas être prévisualisé.</p>
                </div>
              </div>

              <div class="modal-footer bg-light">
                <button type="button" class="btn btn-secondary" @click="closeDocumentModal">
                  <i class="fas fa-times me-2"></i>Fermer
                </button>
              </div>

            </div>
          </div>
        </div>
        <div v-else class="text-center text-muted py-4">
          Aucun document joint.
        </div>
        <div class="mb-4 mt-5">
          <h5 class="text-primary fw-bold border-bottom pb-2">Historique des statuts</h5>
        </div>
        <div class="timeline">
          <div v-for="(h, index) in statusHistory" :key="h.id" class="timeline-item">
            <div class="timeline-marker" :class="index === 0 ? 'bg-success' : 'bg-primary'"></div>
            <div class="timeline-content">
              <p class="fw-bold mb-1">
                {{ h.newStatus?.label || 'Statut inconnu' }}
                <span class="text-muted small">— {{ formatDateTime(h.createdAt) }}</span>
              </p>
              <p class="mb-0 small">
                Par : {{ h.changedByUser?.username || 'Système' }}<br>
                Motif : {{ h.changeReason || '-' }}
                <span v-if="h.comment">• {{ h.comment }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- ====================== RÈGLES APPLIQUÉES & MOMENTS D'EXÉCUTION ====================== -->
        <div class="mb-4 mt-5" v-if="autoFlags.length > 0 || appliedRules.length > 0">
          <h5 class="text-primary fw-bold border-bottom pb-2">
            <i class="fas fa-cogs me-2"></i>
            Règles appliquées — Moments d'exécution
          </h5>
        </div>

        <div v-if="appliedRules.length > 0" class="mb-5">
          <div class="table-responsive">
            <table class="table table-hover align-middle">
              <thead style="background-color: #0a59a4;" class="text-white">
                <tr>
                  <th>Code</th>
                  <th>Libellé</th>
                  <th>Sévérité</th>
                  <th class="text-center">À la création</th>
                  <th class="text-center">À la mise à jour</th>
                  <th class="text-center">Avant émission</th>
                  <th>Pays</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rule in appliedRules" :key="rule.id">
                  <td><code class="badge bg-secondary">{{ rule.code }}</code></td>
                  <td>{{ rule.name }}</td>
                  <td>
                    <span class="badge" :class="getSeverityBadgeClass(rule.severity)">
                      {{ translateSeverity(rule.severity) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <i v-if="rule.run_on_creation" class="fas fa-check-circle text-success fs-5"
                      title="Exécuté à la création"></i>
                    <i v-else class="fas fa-times-circle text-muted fs-5" title="Non exécuté à la création"></i>
                  </td>
                  <td class="text-center">
                    <i v-if="rule.run_on_update" class="fas fa-check-circle text-success fs-5"
                      title="Exécuté à la mise à jour"></i>
                    <i v-else class="fas fa-times-circle text-muted fs-5" title="Non exécuté à la mise à jour"></i>
                  </td>
                  <td class="text-center">
                    <i v-if="rule.run_on_pre_issuance" class="fas fa-check-circle text-danger fs-5"
                      title="Contrôle bloquant avant émission"></i>
                    <i v-else class="fas fa-times-circle text-muted fs-5" title="Non bloquant à l'émission"></i>
                  </td>
                  <td>
                    <span v-if="rule.country_code && rule.country_code !== 'ALL'" class="badge bg-info">
                      {{ rule.country_code }}
                    </span>
                    <span v-else class="text-muted small">Global</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else-if="!loading" class="alert alert-light text-center mb-5">
          <i class="fas fa-cogs fa-2x text-muted mb-2"></i>
          <p class="mb-0 text-muted">Aucune règle configurée pour ce dossier</p>
        </div>

        <!-- ====================== BARRE DE PROGRESSION GLOBALE ====================== -->
        <div v-if="validationTasks.length > 0" class="mb-4 mt-5">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 fw-bold">
                  <i class="fas fa-chart-line me-2 text-primary"></i>
                  Progression globale des vérifications
                </h6>
                <span class="text-muted">
                  {{ globalProgressionStats.passedCount }}/{{ globalProgressionStats.totalChecks }} conformes
                </span>
              </div>

              <!-- Barre tricolore -->
              <div class="progress mb-3" style="height: 30px; border-radius: 10px;">
                <!-- Partie verte (PASSED) -->
                <div class="progress-bar bg-success d-flex align-items-center justify-content-center fw-bold"
                  :style="{ width: globalProgressionStats.passedPercentage + '%' }"
                  v-if="globalProgressionStats.passedPercentage > 0">
                  {{ globalProgressionStats.passedPercentage }}%
                </div>

                <!-- Partie jaune (WARNING) -->
                <div class="progress-bar bg-warning d-flex align-items-center justify-content-center fw-bold text-dark"
                  :style="{ width: globalProgressionStats.warningPercentage + '%' }"
                  v-if="globalProgressionStats.warningPercentage > 0">
                  {{ globalProgressionStats.warningPercentage }}%
                </div>

                <!-- Partie rouge (FAILED) -->
                <div class="progress-bar bg-danger d-flex align-items-center justify-content-center fw-bold text-white"
                  :style="{ width: globalProgressionStats.failedPercentage + '%' }"
                  v-if="globalProgressionStats.failedPercentage > 0">
                  {{ globalProgressionStats.failedPercentage }}%
                </div>
              </div>

              <!-- Légende -->
              <div class="d-flex gap-4 flex-wrap small">
                <div class="d-flex align-items-center gap-2">
                  <div class="bg-success" style="width: 16px; height: 16px; border-radius: 3px;"></div>
                  <span><strong>{{ globalProgressionStats.passedCount }}</strong> Conforme(s)
                    ({{ globalProgressionStats.passedPercentage }}%)</span>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <div class="bg-warning" style="width: 16px; height: 16px; border-radius: 3px;"></div>
                  <span><strong>{{ globalProgressionStats.warningCount }}</strong> Avertissement(s)
                    ({{ globalProgressionStats.warningPercentage }}%)</span>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <div class="bg-danger" style="width: 16px; height: 16px; border-radius: 3px;"></div>
                  <span><strong>{{ globalProgressionStats.failedCount }}</strong> Non conforme(s)
                    ({{ globalProgressionStats.failedPercentage }}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ====================== ALERTE CORRECTIONS GROUPÉES ====================== -->
        <div v-if="pendingCorrections.length > 0" class="alert alert-light border border-primary shadow-sm mb-4 mt-4">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <i class="fas fa-clipboard-check fa-2x text-primary"></i>
              <div>
                <h5 class="mb-1 fw-bold text-primary">
                  <i class="flaticon-check-mark me-2"></i>
                  Corrections en attente
                </h5>

                <p class="mb-0">
                  <strong>{{ pendingCorrections.length }} correction(s)</strong> en attente de traitement
                </p>
                <small class="text-muted">
                  Marquez les corrections comme résolues une fois traitées
                </small>
              </div>
            </div>

            <!-- Liste des corrections en attente -->
            <div class="mt-3">
              <div v-for="correction in pendingCorrections" :key="correction.id"
                class="d-flex align-items-center justify-content-between p-2 mb-2 bg-light rounded">
                <div class="flex-grow-1">
                  <small class="fw-semibold">{{ correction.fields_concerned }}</small>
                  <p class="mb-0 small text-muted">{{ correction.message_to_requester }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ====================== NOUVELLES CORRECTIONS À ENVOYER ====================== -->
        <div v-if="allCorrections.length > 0" class="border border-warning shadow-sm mb-4 mt-4">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <i class="fas fa-exclamation-triangle fa-2x text-primary"></i>
              <div>
                <h5 class="mb-1 fw-bold text-primary">
                  Nouvelles corrections détectées
                </h5>
                <p class="mb-0">
                  <strong>{{ allCorrections.length }} correction(s)</strong> détectée(s) lors des vérifications
                </p>
                <small class="text-muted">
                  Envoyez ces corrections au transitaire pour traitement
                </small>
              </div>
            </div>
            <button @click="showCorrectionRequestModal = true" class="btn btn-primary btn-lg px-2">
              <i class="fas fa-paper-plane me-2"></i>
              Envoyer {{ allCorrections.length }} correction(s)
            </button>
          </div>
        </div>

        <!-- Message si toutes les tâches terminées SANS corrections -->
        <div v-else-if="allCorrections.length === 0 && validationTasks.length > 0 && allTasksCompleted"
          class="shadow-sm mb-4 mt-4">
          <div class="d-flex align-items-center gap-3">
            <i class="fas fa-check-circle fa-2x text-success"></i>
            <div>
              <h5 class="mb-1 fw-bold text-success">
                <i class="flaticon-paper-plane me-2"></i> Dossier conforme
              </h5>
              <p class="mb-0">
                Toutes les vérifications sont terminées et aucune correction n'est nécessaire
              </p>
            </div>
          </div>
        </div>
        <div class="section-title mb-4 mt-5" v-if="validationTasks.length">
          <h5 class="text-primary fw-bold border-bottom pb-2">
            <i class="fas fa-tasks me-2"></i>
            Tâches de validation ({{ validationTasks.length }})
          </h5>
        </div>

        <div v-if="validationTasks.length" class="mb-5">
          <div class="accordion" id="tasksAccordion">
            <div v-for="(task, taskIndex) in validationTasks" :key="task.id"
              class="accordion-item border mb-3 shadow-sm">

              <!-- Header de la tâche -->
              <h2 class="accordion-header">
                <button class="accordion-button collapsed bg-white" type="button" :data-bs-toggle="'collapse'"
                  :data-bs-target="'#task' + taskIndex" :aria-expanded="false" :aria-controls="'task' + taskIndex">
                  <div class="d-flex justify-content-between align-items-center w-100 pe-3">
                    <div>
                      <i class="flaticon-task me-2"></i>
                      {{ task.libelle || 'Tâche sans titre' }}
                    </div>
                    <div class="d-flex gap-2 align-items-center">
                      <!-- Badge statut -->
                      <span class="badge" :class="{
                        'bg-success': task.status === 'COMPLETED',
                        'bg-warning text-dark': task.status === 'IN_PROGRESS',
                        'bg-secondary': task.status === 'PENDING'
                      }">
                        {{ translateStatus(task.status) }}
                      </span>

                      <!-- Badge progression -->
                      <span v-if="task.checks?.length" class="badge bg-primary">
                        {{ getCheckStats(task.checks)?.completion || 0 }}% complété
                      </span>

                      <!-- Badge nombre de commentaires -->
                      <span v-if="getTaskComments(task.id).length" class="badge bg-info">
                        <i class="fas fa-comments me-1"></i>
                        {{ getTaskComments(task.id).length }}
                      </span>
                    </div>
                  </div>
                </button>
              </h2>

              <div :id="'task' + taskIndex" class="accordion-collapse collapse" data-bs-parent="#tasksAccordion">
                <div class="accordion-body">

                  <div class="row mb-4">
                    <div class="col-md-4">
                      <label class="text-muted small fw-semibold">Assigné à</label>
                      <p class="fw-bold mb-0">
                        <i class="fas fa-user me-1 text-primary"></i>
                        {{ task.user ? `${task.user.nom} ${task.user.prenom}` : 'Aucun agent assigné' }}
                      </p>
                    </div>
                    <div class="col-md-4">
                      <label class="text-muted small fw-semibold">Date limite (SLA)</label>
                      <p class="fw-bold mb-0">
                        <i class="fas fa-calendar-alt me-1 text-primary"></i>
                        {{ task.sla_due_at ? formatDate(task.sla_due_at) : 'Non définie' }}
                      </p>
                    </div>
                    <div class="col-md-4">
                      <label class="text-muted small fw-semibold">Priorité</label>
                      <p class="mb-0">
                        <span class="badge" :class="{
                          'bg-danger': task.priority === 'HIGH',
                          'bg-warning text-dark': task.priority === 'MEDIUM',
                          'bg-info': task.priority === 'LOW'
                        }">
                          {{ task.priority === 'HIGH' ? 'Haute' : task.priority === 'MEDIUM' ? 'Moyenne' : 'Basse' }}
                        </span>
                      </p>
                    </div>
                    <div class="col-12 mt-3" v-if="task.description">
                      <label class="text-muted small fw-semibold">Description</label>
                      <p class="mb-0 p-3 bg-light rounded">{{ task.description }}</p>
                    </div>
                  </div>

                  <!-- Barre de progression détaillée -->
                  <div v-if="task.checks?.length" class="mb-4">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <label class="text-muted small fw-semibold">
                        <i class="fas fa-chart-line me-1"></i>
                        Progression des contrôles
                      </label>
                      <small class="text-muted">
                        {{ getTaskProgress(task).completed }} / {{ getTaskProgress(task).total }} traités
                      </small>
                    </div>

                    <div class="progress mb-2" style="height: 8px;">
                      <div class="progress-bar progress-bar-striped" :class="{
                        'bg-success': getTaskProgress(task).percentage === 100,
                        'bg-warning': getTaskProgress(task).percentage > 0 && getTaskProgress(task).percentage < 100,
                        'bg-secondary': getTaskProgress(task).percentage === 0
                      }" :style="{ width: getTaskProgress(task).percentage + '%' }" role="progressbar"
                        :aria-valuenow="getTaskProgress(task).percentage" aria-valuemin="0" aria-valuemax="100">
                      </div>
                    </div>

                    <div class="d-flex justify-content-between align-items-center">
                      <small class="text-muted">{{ getTaskProgress(task).percentage }}% complété</small>
                    </div>
                  </div>

                  <div v-if="task.checks?.length" class="border-top pt-4 mb-4">

                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h6 class="mb-0">
                        <i class="fas fa-clipboard-check me-2 text-primary"></i>
                        Check-list de validation ({{ task.checks.length }})
                      </h6>

                      <div class="d-flex gap-2 flex-wrap" v-if="getCheckStats(task.checks)">
                        <span class="badge bg-success">
                          <i class="fas fa-check me-1"></i>
                          {{ getCheckStats(task.checks)?.passed ?? 0 }} Conforme(s)
                        </span>
                        <span class="badge bg-danger">
                          <i class="fas fa-times me-1"></i>
                          {{ getCheckStats(task.checks)?.failed ?? 0 }} Non conforme(s)
                        </span>
                        <span class="badge bg-warning text-dark">
                          <i class="fas fa-exclamation-triangle me-1"></i>
                          {{ getCheckStats(task.checks)?.warning ?? 0 }} Avertissement(s)
                        </span>
                        <span class="badge bg-secondary">
                          <i class="fas fa-clock me-1"></i>
                          {{ getCheckStats(task.checks)?.pending ?? 0 }} En attente
                        </span>
                      </div>
                    </div>

                    <!-- Message d'alerte pour tâche complétée -->
                    <div v-if="task.status === 'COMPLETED'" class="alert alert-success text-dark">
                      <i class="flaticon-lock me-2"></i>
                      Cette tâche est terminée. Les contrôles ne peuvent plus être modifiés.
                    </div>


                    <div class="row g-3">
                      <div v-for="(check, checkIndex) in task.checks" :key="check.id" class="col-lg-6">
                        <div class="card h-100 shadow-sm check-card" :class="{
                          'border-success border-2': check.result === 'PASSED',
                          'border-danger border-2': check.result === 'FAILED',
                          'border-warning border-2': check.result === 'WARNING',
                          'border-secondary': check.result === 'PENDING',
                          'border-info border-2': check.result === 'NOT_APPLICABLE'
                        }">
                          <div class="card-body p-3">

                            <div class="d-flex justify-content-between align-items-start mb-3">
                              <div class="d-flex align-items-start gap-2 flex-grow-1">
                                <span class="badge bg-primary fs-6">{{ Number(checkIndex) + 1 }}</span>

                                <div class="flex-grow-1">
                                  <h6 class="mb-2 fw-bold">
                                    <i :class="[
                                      getCheckResultIcon(check.result),
                                      'me-2',
                                      {
                                        'text-success': check.result === 'PASSED',
                                        'text-danger': check.result === 'FAILED',
                                        'text-warning': check.result === 'WARNING',
                                        'text-secondary': check.result === 'PENDING',
                                        'text-info': check.result === 'NOT_APPLICABLE'
                                      }
                                    ]"></i>
                                    {{ check.title }}
                                  </h6>

                                  <div class="d-flex gap-2 mb-2 flex-wrap">
                                    <span class="badge small"
                                      :class="check.check_type === 'MANUAL' ? 'bg-primary' : 'bg-info'">
                                      <i :class="check.check_type === 'MANUAL' ? 'fas fa-user' : 'fas fa-robot'"></i>
                                      {{ check.check_type === 'MANUAL' ? 'Manuel' : 'Automatique' }}
                                    </span>

                                    <span class="badge small" :class="getSeverityBadgeClass(check.severity)">
                                      <i class="fas fa-exclamation-circle me-1"></i>
                                      {{ translateSeverity(check.severity) }}
                                    </span>

                                    <span v-if="check.check_code" class="badge bg-secondary small">
                                      {{ check.check_code }}
                                    </span>
                                  </div>

                                  <p class="mb-0 text-muted small">
                                    {{ check.description || 'Aucune description' }}
                                  </p>
                                </div>
                              </div>

                              <span class="badge fs-6 px-3 py-2" :class="getCheckResultClass(check.result)">
                                {{ translateStatus(check.result) }}
                              </span>
                            </div>

                            <div class="border-top pt-3">

                              <div v-if="(check.result === 'FAILED' || check.result === 'WARNING') &&
                                (!check.comments || check.comments.trim() === '')" class="alert alert-danger p-2 mb-3">
                                <i class="fas fa-exclamation-circle me-1"></i>
                                <strong>Commentaire obligatoire !</strong>
                                <small class="d-block mt-1">
                                  Un commentaire est requis pour expliquer cette non-conformité au transitaire.
                                </small>
                              </div>

                              <div class="mb-3">
                                <label class="form-label small fw-semibold mb-2">
                                  <i class="fas fa-check-circle me-1"></i>
                                  Résultat du contrôle
                                  <span v-if="check.check_type === 'AUTOMATIC'" class="badge bg-info ms-2 small">
                                    <i class="fas fa-robot me-1"></i>
                                    Automatique
                                  </span>
                                </label>

                                <Multiselect :model-value="check.result"
                                  @update:model-value="(newValue) => updateCheck(task.id, check.id, newValue, check.comments)"
                                  :options="resultatCheckOptions" :disabled="task.status === 'COMPLETED'" label="label"
                                  track-by="value" value-prop="value" :searchable="false" :allow-empty="false" />
                              </div>

                              <div class="mb-2">
                                <label class="form-label small fw-semibold mb-2">
                                  <i class="fas fa-comment me-1"></i>
                                  Commentaire / Justification
                                </label>

                                <textarea class="form-control form-control-sm" rows="3" :value="check.comments"
                                  @blur="(e) => updateCheck(task.id, check.id, check.result, (e.target as HTMLTextAreaElement)?.value || '')"
                                  :disabled="task.status === 'COMPLETED'"
                                  placeholder="Ajoutez une justification, remarque ou observation..." />

                                <small class="text-muted d-block mt-1">
                                  {{ check.comments?.length || 0 }} caractère(s)
                                </small>
                              </div>
                              <div v-if="check.checked_at" class="mt-2 p-2 bg-light rounded">
                                <small class="text-muted">
                                  <i class="fas fa-clock me-1"></i>
                                  <strong>Dernière mise à jour :</strong> {{ formatDateTime(check.checked_at) }}
                                </small>
                              </div>
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>

                  </div>

                  <div v-else class="alert alert-warning text-center">
                    <i class="fas fa-exclamation-triangle fa-2x mb-2"></i>
                    <p class="mb-0">Aucune vérification configurée pour cette tâche</p>
                  </div>

                  <div class="border-top pt-4 mt-4">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h6 class="mb-0">
                        <i class="fas fa-comments me-2 text-primary"></i>
                        Commentaires sur cette tâche ({{ getTaskComments(task.id).length }})
                      </h6>
                      <div class="btn-group gap-2">
                        <button @click="openCommentModalWithTask('BESC_FILE', null, task.id)"
                          class="btn btn-sm btn-outline-primary">
                          <i class="fas fa-plus me-1"></i>
                          Ajouter un commentaire
                        </button>
                        <!-- <button v-if="isAdmin || isAgent" @click="openCorrectionRequestModal(task.id)"
                          class="btn btn-sm btn-outline-success" :disabled="loading">
                          <i class="fas fa-exclamation-circle me-1"></i>
                          Demander une correction
                        </button> -->
                      </div>
                    </div>
                    <div v-if="getTaskComments(task.id).length">
                      <div v-for="comment in getTaskComments(task.id)" :key="comment.id" class="card mb-2 shadow-sm"
                        :class="comment.visibility === 'EXTERNAL' ? 'border-warning' : 'border-secondary'">
                        <div class="card-body p-3">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <span class="badge me-2"
                                :class="comment.visibility === 'EXTERNAL' ? 'bg-warning text-dark' : 'bg-secondary'">
                                <i :class="comment.visibility === 'EXTERNAL' ? 'fas fa-eye' : 'fas fa-lock'"></i>
                                {{ comment.visibility === 'EXTERNAL' ? 'Externe' : 'Interne' }}
                              </span>

                              <!-- Badge entité liée -->
                              <span v-if="comment.relatedEntityType !== 'BESC_FILE'"
                                class="badge bg-info text-white me-2">
                                <i class="fas fa-link me-1"></i>
                                {{ comment.relatedEntityType === 'BILL_OF_LADING' ? 'BL' :
                                  comment.relatedEntityType === 'SUPPORTING_DOCUMENT' ? 'Document' :
                                    'Autre' }}
                              </span>
                              <strong>{{ comment.author?.username || 'Utilisateur' }}</strong>
                              <small class="text-muted ms-2">
                                {{ formatDateTime(comment.createdAt) }}
                              </small>
                            </div>

                            <div class="btn-group btn-group-sm">
                              <button @click="editComment(comment)" class="btn btn-outline-primary" title="Modifier">
                                <i class="fas fa-edit"></i>
                              </button>
                              <button @click="deleteComment(comment.id)" class="btn btn-outline-danger"
                                title="Supprimer">
                                <i class="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                          <p class="mb-0">{{ comment.commentText }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- Message si aucun commentaire -->
                    <div v-else class="alert alert-light text-center">
                      <i class="fas fa-comment-slash text-muted fa-2x mb-2"></i>
                      <p class="mb-0 text-muted">Aucun commentaire pour cette tâche</p>
                    </div>
                  </div>

                  <div v-if="task.status !== 'COMPLETED'" class="border-top pt-3 mt-3 text-end">
                    <button v-if="task.status !== 'COMPLETED'" @click="completeTask(task.id)"
                      class="btn btn-success btn-lg px-5" :disabled="!getTaskProgress(task).canComplete">
                      <i class="fas fa-check-double me-2"></i>
                      Marquer comme complétée
                    </button>
                    <small v-if="!getTaskProgress(task).canComplete" class="d-block text-muted mt-2">
                      <i class="fas fa-info-circle me-1"></i>
                      Tous les contrôles doivent être traités avant de finaliser la tâche
                    </small>
                  </div>

                  <!-- Badge tâche complétée -->
                  <div v-else class="alert alert-success text-center mb-0">
                    <i class="fas fa-check-circle fa-2x mb-2"></i>
                    <h6 class="mb-1">Tâche complétée</h6>
                    <small>
                      Le {{ formatDateTime(task.completed_at) }}
                    </small>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Message si aucune tâche -->
        <div v-else-if="!loading" class="alert alert-info text-center py-5">
          <i class="fas fa-info-circle fa-3x mb-3"></i>
          <h5>Aucune tâche de validation</h5>
          <p class="mb-0 text-muted">Ce BESC n'a pas encore de tâches de validation assignées</p>
        </div>

        <!-- Loader pendant le chargement -->
        <div v-else class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Chargement...</span>
          </div>
          <p class="mt-3 text-muted">Chargement des tâches de validation...</p>
        </div>

        <div class="d-flex justify-content-between mt-5 pt-4 border-top">
          <router-link to="/liste-besc" class="btn btn-secondary px-4">
            Retour à la liste
          </router-link>

          <div class="btn-group">
            <!-- <button @click="printPDF" class="btn btn-success px-4">
              Imprimer le BESC
            </button> -->
            <button v-if="besc.currentStatus?.code === 'DRAFT'" @click="goToEdit"
              class="btn btn-warning px-4 text-white">
              Modifier
            </button>
            <!-- <button v-if="isAdmin" @click="showCorrectionRequestModal = true" class="btn btn-outline-success px-4"
              :disabled="loading">
              <i class="fas fa-exclamation-circle me-2"></i>
              Demander une correction
            </button> -->
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showFlagsModal" class="modal-backdrop fade show" @click="showFlagsModal = false"></div>

  <div v-if="showFlagsModal" class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">

        <div class="modal-header bg-gradient" :class="totalBlockingFlags > 0 ? 'bg-white' : 'bg-warning'">
          <div class="text-black">
            <h5 class="modal-title mb-2">
              <i class="fas fa-clipboard-check me-2"></i>
              Rapport des contrôles automatiques
            </h5>
            <div class="d-flex gap-4 small">
              <span v-if="totalBlockingFlags > 0">
                <i class="fas fa-ban me-1"></i>
                {{ totalBlockingFlags }} Bloquante(s)
              </span>
              <span v-if="totalHighRiskFlags > 0">
                <i class="fas fa-exclamation-triangle me-1"></i>
                {{ totalHighRiskFlags }} Risque élevé
              </span>
              <span v-if="totalWarningFlags > 0">
                <i class="fas fa-info-circle me-1"></i>
                {{ totalWarningFlags }} Avertissement(s)
              </span>
            </div>
          </div>
          <button type="button" class="btn-close btn-close-white" @click="showFlagsModal = false"></button>
        </div>

        <div class="modal-body p-4">

          <div v-if="totalBlockingFlags > 0" class="mb-5">
            <div class="d-flex align-items-center mb-3 pb-2 border-bottom border-danger">
              <i class="fas fa-ban text-danger fa-lg me-2"></i>
              <h6 class="mb-0 fw-bold text-danger">
                ALERTES BLOQUANTES ({{ totalBlockingFlags }})
              </h6>
            </div>
            <p class="text-muted small mb-3">
              Ces anomalies empêchent l'émission du BESC. Elles doivent être résolues par un agent habilité.
            </p>

            <div v-for="(flag, index) in blockingFlags" :key="flag.id" class="card border-danger mb-3 shadow-sm">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div class="d-flex align-items-start gap-2">
                    <span class="badge bg-danger fs-6">{{ index + 1 }}</span>
                    <div>
                      <h6 class="mb-1 fw-bold text-danger">
                        <i class="fas fa-ban me-2"></i>
                        {{ flag.title }}
                      </h6>
                      <span class="badge bg-danger">{{ flag.severity }}</span>
                      <span class="badge bg-dark ms-1">{{ flag.flag_type }}</span>
                    </div>
                  </div>
                  <span class="badge bg-danger">BLOQUANT</span>
                </div>

                <div class="alert alert-light mb-3">
                  <strong>Description :</strong><br>
                  {{ flag.description }}
                </div>

                <div class="row text-muted small">
                  <div class="col-md-6">
                    <i class="fas fa-cogs me-1"></i>
                    <strong>Règle :</strong> {{ flag.rule?.name || 'Non spécifiée' }}
                  </div>
                  <div class="col-md-6">
                    <i class="fas fa-clock me-1"></i>
                    <strong>Détecté le :</strong> {{ formatDateTime(flag.createdAt) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="totalHighRiskFlags > 0" class="mb-5">
            <div class="d-flex align-items-center mb-3 pb-2 border-bottom border-danger">
              <i class="fas fa-exclamation-triangle text-danger fa-lg me-2"></i>
              <h6 class="mb-0 fw-bold text-danger">
                RISQUES ÉLEVÉS - BLOQUANTS ({{ totalHighRiskFlags }})
              </h6>
            </div>
            <p class="text-muted small mb-3">
              Ces alertes signalent des risques élevés qui <strong>empêchent également l'émission du BESC</strong>.
              Elles nécessitent une validation obligatoire.
            </p>

            <div v-for="(flag, index) in highRiskFlags" :key="flag.id" class="card border-danger mb-3 shadow-sm">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div class="d-flex align-items-start gap-2">
                    <span class="badge bg-danger fs-6">{{ totalBlockingFlags + index + 1 }}</span>
                    <div>
                      <h6 class="mb-1 fw-bold text-danger">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        {{ flag.title }}
                      </h6>
                      <span class="badge bg-danger">{{ flag.severity }}</span>
                      <span class="badge bg-dark ms-1">{{ flag.flag_type }}</span>
                    </div>
                  </div>
                  <span class="badge bg-danger">BLOQUANT</span>
                </div>

                <div class="alert alert-light mb-3">
                  <strong>Description :</strong><br>
                  {{ flag.description }}
                </div>

                <div class="row text-muted small">
                  <div class="col-md-6">
                    <i class="fas fa-cogs me-1"></i>
                    <strong>Règle :</strong> {{ flag.rule?.name || 'Non spécifiée' }}
                  </div>
                  <div class="col-md-6">
                    <i class="fas fa-clock me-1"></i>
                    <strong>Détecté le :</strong> {{ formatDateTime(flag.createdAt) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="totalWarningFlags > 0" class="mb-4">
            <div class="d-flex align-items-center mb-3 pb-2 border-bottom border-info">
              <i class="fas fa-info-circle text-info fa-lg me-2"></i>
              <h6 class="mb-0 fw-bold text-info">
                AVERTISSEMENTS ({{ totalWarningFlags }})
              </h6>
            </div>
            <p class="text-muted small mb-3">
              Points d'attention ne bloquant pas l'émission mais méritant d'être vérifiés.
            </p>

            <div class="accordion" id="warningsAccordion">
              <div v-for="(flag, index) in warningFlags" :key="flag.id" class="accordion-item">
                <h2 class="accordion-header">
                  <button class="accordion-button collapsed" type="button" :data-bs-toggle="'collapse'"
                    :data-bs-target="'#warning' + flag.id">
                    <span class="badge bg-info me-2">{{ index + 1 }}</span>
                    {{ flag.title }}
                  </button>
                </h2>
                <div :id="'warning' + flag.id" class="accordion-collapse collapse" data-bs-parent="#warningsAccordion">
                  <div class="accordion-body">
                    <p>{{ flag.description }}</p>
                    <small class="text-muted">
                      Règle : {{ flag.rule?.name }} •
                      Détecté le {{ formatDateTime(flag.createdAt) }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="totalAllFlags === 0" class="text-center py-5">
            <i class="fas fa-check-circle fa-4x text-success mb-3"></i>
            <h5 class="text-success">Aucune alerte détectée</h5>
            <p class="text-muted">
              Tous les contrôles automatiques sont passés avec succès.
            </p>
          </div>

        </div>

        <div class="modal-footer bg-light">
          <div class="d-flex justify-content-between w-100 align-items-center">
            <small class="text-muted">
              <i class="fas fa-robot me-1"></i>
              Contrôles générés automatiquement par le moteur de règles
            </small>
            <button type="button" class="btn btn-secondary" @click="showFlagsModal = false">
              <i class="fas fa-times me-2"></i>
              Fermer
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
  <div v-if="showCorrectionRequestModal" class="modal-backdrop fade show" @click="showCorrectionRequestModal = false">
  </div>
  <div v-if="showCorrectionRequestModal" class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header bg-primary py-2">
          <div class="text-white">
            <h6 class="modal-title mb-0 text-white">
              <i class="fas fa-paper-plane me-2"></i>
              Demande de Correction Groupée ({{ allCorrections.length }})
            </h6>
          </div>
          <button type="button" class="btn-close btn-close-white" @click="showCorrectionRequestModal = false"></button>
        </div>

        <div class="modal-body p-3">
          <!-- Liste des corrections compacte -->
          <div class="mb-3">
            <h6 class="text-primary fw-semibold mb-2">
              <i class="fas fa-list me-2"></i>
              Corrections détectées
            </h6>

            <div class="corrections-list border rounded p-2"
              style="max-height: 200px; overflow-y: auto; background-color: #f8f9fa;">
              <div v-for="(correction, index) in allCorrections" :key="index"
                class="d-flex align-items-start gap-2 mb-2 p-2 bg-white rounded border-start border-3"
                :class="correction.result === 'FAILED' ? 'border-danger' : 'border-warning'">
                <span class="badge bg-primary d-flex align-items-center justify-content-center flex-shrink-0"
                  style="width: 20px; height: 20px; border-radius: 4px; font-size: 10px;">
                  {{ index + 1 }}
                </span>

                <div class="flex-grow-1 small">
                  <div class="fw-semibold text-dark">{{ correction.checkTitle }}</div>
                  <div class="text-muted">{{ correction.taskName }}</div>
                  <div class="text-primary mt-1">{{ correction.correctionNeeded }}</div>
                </div>

                <span class="badge d-flex align-items-center justify-content-center flex-shrink-0"
                  :class="correction.result === 'FAILED' ? 'bg-danger' : 'bg-warning text-dark'"
                  style="width: 18px; height: 18px; border-radius: 4px; font-size: 9px;">
                  {{ correction.result === 'FAILED' ? 'C' : 'A' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Formulaire simplifié -->
          <Form ref="CorrectionRequestForm" @submit="addCorrectionRequest"
            :validation-schema="validationCorrectionSchema">
            <div class="row g-2">
              <div class="col-md-6">
                <label class="form-label small fw-semibold">
                  Date d'émission <span class="text-danger">*</span>
                </label>
                <Field name="requested_at" type="date" class="form-control form-control-sm" />
                <ErrorMessage name="requested_at" class="text-danger small" />
              </div>

              <div class="col-md-6">
                <label class="form-label small fw-semibold">
                  Date limite <span class="text-danger">*</span>
                </label>
                <Field name="due_date" type="date" class="form-control form-control-sm" />
                <ErrorMessage name="due_date" class="text-danger small" />
              </div>

              <div class="col-12">
                <label class="form-label small fw-semibold">
                  Message au transitaire <span class="text-danger">*</span>
                </label>
                <Field name="message_to_requester" as="textarea" class="form-control form-control-sm" rows="4"
                  placeholder="Décrivez les corrections à apporter..." />
                <ErrorMessage name="message_to_requester" class="text-danger small" />
                <small class="text-muted">
                  <i class="fas fa-info-circle me-1"></i>
                  Les détails des corrections seront automatiquement inclus à partir des commentaires ajoutés
                </small>
              </div>
            </div>

            <div class="modal-footer bg-light mt-3 py-2">
              <button type="button" class="btn btn-secondary btn-sm" @click="showCorrectionRequestModal = false">
                <i class="fas fa-times me-1"></i>
                Annuler
              </button>
              <button type="submit" class="btn btn-primary btn-sm">
                <i class="fas fa-paper-plane me-1"></i>
                Envoyer la demande de correction
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>


  <div v-if="showValidationModal" class="modal-backdrop fade show" @click="showValidationModal = false"></div>

  <div v-if="showValidationModal" class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white">
            <i class="fas fa-clipboard-check me-2"></i>
            Validation du dossier
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="showValidationModal = false"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-info">
            <i class="fas fa-info-circle me-2"></i>
            Vous êtes sur le point de prendre une décision sur ce dossier BESC après avoir effectué tous les contrôles.
          </div>
          <div class="mb-3">
            <div class="row">
              <div class="col-6">
                <div class="card bg-light">
                  <div class="card-body text-center py-2">
                    <h6 class="mb-1">{{ validationTasks.length }}</h6>
                    <small class="text-muted">Tâches complétées</small>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="card bg-success text-white">
                  <div class="card-body text-center py-2">
                    <h6 class="mb-1">{{ getTotalChecksCount() }}</h6>
                    <small>Contrôles</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label fw-semibold">Commentaire (optionnel)</label>
            <textarea v-model="validationComment" class="form-control" rows="4"
              placeholder="Ajoutez un commentaire sur votre décision..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showValidationModal = false">
            Annuler
          </button>
          <!-- <button type="button" class="btn btn-danger me-2" @click="validateDossier('REJECTED')"
            :disabled="validatingDossier">
            <i class="fas fa-times me-2"></i>
            {{ validatingDossier ? 'Traitement...' : 'Rejeter le dossier' }}
          </button> -->
          <button v-if="totalCriticalFlags > 0 || allCorrections.length > 0" type="button" class="btn btn-danger me-2"
            @click="validateDossier('REJECTED')">
            Rejeter le dossier
          </button>
          <button type="button" class="btn btn-success" @click="validateDossier('VALIDATED')"
            :disabled="validatingDossier">
            <i class="fas fa-check me-2"></i>
            {{ validatingDossier ? 'Traitement...' : 'Valider le dossier' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Form, Field, ErrorMessage } from 'vee-validate';
import ApiService from '@/services/ApiService'
import { error, monSwal, success } from '@/utils/utils'
import Multiselect from '@vueform/multiselect'
import * as Yup from 'yup';
import JwtService from '@/services/JwtService';
import { onActivated, watch } from 'vue'
import { toast } from 'sonner';
import axios from 'axios';
import CheckoutContent from '../Ecommerce/Checkout/CheckoutContent.vue';

export default defineComponent({
  name: 'ViewBesc',
  components: {
    Multiselect,
    Form,
    Field,
    ErrorMessage,
  },

  setup() {
    const validationCorrectionSchema = Yup.object().shape({
      requested_at: Yup.string().required('Ce champ est obligatoire'),
      due_date: Yup.string().required('Ce champ est obligatoire')
        .test(
          'is-after-requested',
          'La date d\'échéance doit être supérieure à la date demandée',
          function (value) {
            const { requested_at } = this.parent;
            if (!requested_at || !value) return true;
            return new Date(value) > new Date(requested_at);
          }
        ),
      message_to_requester: Yup.string().required('Ce champ est obligatoire'),
    });

    const route = useRoute()
    const router = useRouter()
    const bescId = route.params.id as string
    const getBescId = () => route.params.id as string

    const isAdmin = computed(() => {
      const user = JwtService.getUser() as any;
      console.log("Rôles actuels :", user?.roles);
      return user?.roles?.includes('Administrateur') ?? false;
    });

    const isAgent = computed(() => {
      const user = JwtService.getUser() as any;
      console.log("Rôles actuels :", user?.roles);
      return user?.roles?.includes('Agent') ?? false;
    });

    const isSuperviseur = computed(() => {
      const user = JwtService.getUser() as any;
      return user?.roles?.includes('Superviseur') ?? false;
    });

    const besc = ref<any>({})
    const statusHistory = ref<any[]>([])
    const appliedRules = ref<any[]>([])
    const loading = ref(true)
    const bescActeurs = ref<any[]>([])
    const autoFlags = ref<any[]>([])
    const showFlagsModal = ref(false)

    const showDocumentModal = ref(false)
    const currentDocumentUrl = ref('')
    const currentDocumentName = ref('')
    const currentDocumentType = ref<'pdf' | 'image' | 'other'>('pdf')
    const loadingDocument = ref(false)

    const showConteneursModal = ref(false)
    const selectedConteneurs = ref<any[]>([])
    const selectedBlNumber = ref('')

    const showCommentModal = ref(false)
    const selectedBl = ref<any>(null)

    // Variable pour détecter la re-vérification
    const isReverificationNeeded = computed(() => {
      return besc.value?.has_been_corrected === true &&
        besc.value?.currentStatus?.code === 'UNDER_REVIEW'
    })
    const selectedBlIndex = ref<number>(-1)

    const currentComment = ref('')
    const validationtaskOptions = ref([])
    const showCorrectionRequestModal = ref(false)
    const selectedValidationTask = ref<string | null>(null);
    const correctionRetourne = ref<any[]>([])

    const comments = ref<any[]>([])
    const showCommentFormModal = ref(false)
    const commentForm = ref<{
      visibility: string
      comment_text: string
      related_entity_type: string
      related_entity_id: string | null
      validation_task_id: string | null
    }>({
      visibility: 'INTERNAL',
      comment_text: '',
      related_entity_type: 'BESC_FILE',
      related_entity_id: null,
      validation_task_id: null
    })
    //edition
    const editingCommentId = ref<string | null>(null)
    const validationTasks = ref<any[]>([])
    const loadingChecks = ref(false)

    const showValidationModal = ref(false)
    const validatingDossier = ref(false)
    const validationComment = ref('')


    const openCorrectionRequestModal = (taskId: string) => {
      selectedValidationTask.value = taskId;
      showCorrectionRequestModal.value = true;
    }

    const loadAppliedRules = async () => {
      try {
        const res = await ApiService.get(`/besc/${bescId}/applied-rules`)
        appliedRules.value = res.data.data || []
      } catch (err) {
        // Silencieux si endpoint non disponible
        appliedRules.value = []
      }
    }

    const openDocumentModal = async (bescId: string, documentId: string, filename: string) => {
      showDocumentModal.value = true
      loadingDocument.value = true
      currentDocumentName.value = filename || 'Document'
      currentDocumentUrl.value = ''

      // Déterminer le type selon l'extension
      const ext = filename?.split('.').pop()?.toLowerCase() || ''
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
        currentDocumentType.value = 'image'
      } else if (ext === 'pdf') {
        currentDocumentType.value = 'pdf'
      } else {
        currentDocumentType.value = 'other'
      }

      try {
        const token = localStorage.getItem("id_token")
        const response = await ApiService.vueInstance.axios.get(
          `/besc/${bescId}/documents/${documentId}/view`,
          {
            responseType: "blob",
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const blob = new Blob([response.data], { type: response.headers["content-type"] })
        currentDocumentUrl.value = URL.createObjectURL(blob)
      } catch (err) {
        console.error("Erreur chargement document:", err)
        error("Impossible de charger le document")
        showDocumentModal.value = false
      } finally {
        loadingDocument.value = false
      }
    }

    const closeDocumentModal = () => {
      showDocumentModal.value = false
      if (currentDocumentUrl.value) {
        URL.revokeObjectURL(currentDocumentUrl.value)
        currentDocumentUrl.value = ''
      }
    }
    // const addCorrectionRequest = async (values) => {
    //   if (!validationTasks.value.length) {
    //     return error("Impossible de récupérer la tâche de validation liée à ce BESC.")
    //   }

    //   const payload = {
    //     besc_file_id: getBescId(),
    //     validation_task_id: selectedValidationTask.value,
    //     requested_at: values.requested_at,
    //     due_date: values.due_date,
    //     fields_concerned: values.fields_concerned,
    //     message_to_requester: values.message_to_requester,
    //     response_message: null
    //   }

    //   await ApiService.post(`/correction`, payload)
    //   success('Demande enregistrée avec succès !')
    //   showCorrectionRequestModal.value = false
    // }

    const addCorrectionRequest = async (values) => {
      if (allCorrections.value.length === 0) {
        return error("Aucune correction à envoyer")
      }

      // Payload simplifié - le backend construira fields_concerned automatiquement
      const payload = {
        besc_file_id: getBescId(),
        requested_at: values.requested_at,
        due_date: values.due_date,
        message_to_requester: values.message_to_requester,
      }

      try {
        await ApiService.post(`/correction`, payload)
        success(`Demande de correction envoyée avec succès !`)
        showCorrectionRequestModal.value = false

        // Recharger les données
        await loadBesc()
        await loadComments()
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de l\'envoi de la demande de correction')
      }
    }



    const loadValidationTasks = async () => {
      try {
        loading.value = true

        console.log('Chargement des tâches pour BESC:', bescId)

        const res = await ApiService.get(`/besc/${bescId}/validation-tasks`)
        console.log('Réponse API:', res)

        const tasks = res.data.data || res.data || []

        console.log(`${tasks.length} tâche(s) reçue(s)`)

        validationTasks.value = tasks.map((task: any) => {
          const checks = task.validationchecks?.map((check: any) => {
            console.log(' Check data from API:', {
              check_id: check.id,
              tache_id: check.tache?.id,
              task_id: task.id,
              check_title: check.title
            });

            return {
              id: check.id,
              validationTaskId: task.id,
              tache_id: check.tache?.id,
              check_code: check.check_code,
              title: check.title,
              description: check.description,
              check_type: check.check_type,
              severity: check.severity,
              result: check.result || 'PENDING',
              comments: check.comments || '',
              checked_at: check.checked_at,
              tache: check.tache
            }
          }) || []

          console.log(`Tâche "${task.libelle}": ${checks.length} check(s)`)

          return {
            ...task,
            checks
          }
        })

        console.log('Tâches chargées avec succès:', validationTasks.value)

      } catch (err: any) {
        console.error(' Erreur chargement tâches:', err)
        error('Erreur lors du chargement des tâches de validation')
      } finally {
        loading.value = false
      }
    }

    const updateCheck = async (taskId: string, checkId: string, result: string, comments = '') => {
      try {
        console.log(` Mise à jour check ${checkId}:`, { result, comments, taskId })

        const payload = {
          result,
          comments,
          checked_at: new Date().toISOString()
        }

        console.log('Payload envoyé:', payload)

        await ApiService.patch(`validationc/${checkId}`, payload)

        const task = validationTasks.value.find(t => t.id === taskId)

        if (task && task.checks) {
          const check = task.checks.find(c => c.id === checkId)
          if (check) {
            check.result = result
            check.comments = comments
            check.checked_at = payload.checked_at
            console.log('Check mis à jour localement')
          }

          // Mettre à jour le statut de la tâche si nécessaire
          if (task.status === 'PENDING') {
            const hasStartedChecks = task.checks.some(c => c.result && c.result !== 'PENDING')
            if (hasStartedChecks) {
              task.status = 'IN_PROGRESS'
              await ApiService.patch(`validation-tasks/${taskId}`, { status: 'IN_PROGRESS' })
              console.log('Statut de la tâche changé à IN_PROGRESS')
            }
          }
        }

        toast.success('Enregistré', { duration: 1500 })

      } catch (err: any) {
        console.error('Erreur mise à jour check:', err)
        console.error('Détails erreur:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        })

        const errorMsg = err.response?.data?.message || err.message || 'Erreur lors de la mise à jour du contrôle'
        error(errorMsg)
      }
    }

    const completeTask = async (taskId: string) => {
      try {
        const task = validationTasks.value.find(t => t.id === taskId)

        if (!task) {
          error('Tâche non trouvée')
          return
        }

        const pendingChecks = task.checks?.filter(c => c.result === 'PENDING' || !c.result) || []

        if (pendingChecks.length > 0) {
          error(`Il reste ${pendingChecks.length} vérification(s) en attente`)
          return
        }

        const confirmed = await monSwal(
          'Tous les contrôles ont été traités. Voulez-vous marquer cette tâche comme complétée ?',
          'Marquer comme complétée',
          'btn btn-success',
          'Annuler',
          'L\'opération a été annulée'
        )

        if (!confirmed) return

        await ApiService.patch(`validation-tasks/${taskId}/complete`)

        task.status = 'COMPLETED'
        task.completed_at = new Date().toISOString()

        success('Tâche marquée comme complétée')

      } catch (err: any) {
        console.error(' Erreur complétion tâche:', err)
        const errorMessage = err.response?.data?.message || 'Erreur lors de la complétion de la tâche'
        error(errorMessage)
      }
    }

    const getTaskProgress = (task: any) => {
      if (!task.checks || task.checks.length === 0) {
        return { percentage: 0, completed: 0, total: 0, canComplete: false }
      }

      const totalChecks = task.checks.length
      const completedChecks = task.checks.filter((check: any) =>
        check.result && check.result !== 'PENDING'
      ).length

      const percentage = Math.round((completedChecks / totalChecks) * 100)
      const canComplete = completedChecks === totalChecks

      return {
        percentage,
        completed: completedChecks,
        total: totalChecks,
        canComplete
      }
    }

    const visibiliteOptions = [
      { value: 'INTERNAL', label: 'Interne (visible uniquement par les agents)' },
      { value: 'EXTERNAL', label: 'Externe (visible par le demandeur)' }
    ]

    const resultatCheckOptions = [
      { value: 'PENDING', label: 'En attente' },
      { value: 'PASSED', label: ' Conforme' },
      { value: 'WARNING', label: ' Avertissement' },
      { value: 'FAILED', label: ' Non conforme' },
      { value: 'NOT_APPLICABLE', label: ' Non applicable' }
    ]

    const taskOptions = computed(() => {
      const options = [
        { value: null, label: 'Aucune tâche (commentaire général)' }
      ]

      validationTasks.value.forEach(task => {
        options.push({
          value: task.id,
          label: `${task.libelle} - ${translateStatus(task.status)}${task.user ? ` (Assigné à : ${task.user.nom} ${task.user.prenom})` : ''}`
        })
      })

      return options
    })

    const getCheckResultClass = (result: string) => {
      const classes: any = {
        'PASSED': 'bg-success',
        'FAILED': 'bg-danger',
        'WARNING': 'bg-warning text-dark',
        'PENDING': 'bg-secondary',
        'NOT_APPLICABLE': 'bg-info'
      }
      return classes[result] || 'bg-secondary'
    }

    const translateStatus = (status: string) => {
      const map: { [key: string]: string } = {
        PENDING: 'En attente',
        PASSED: 'Conforme',
        FAILED: 'Non conforme',
        WARNING: 'Avertissement',
        NOT_APPLICABLE: 'Non applicable',
        COMPLETED: 'Terminé',
        IN_PROGRESS: 'En cours'
      }
      return map[status] || status
    }

    const translateSeverity = (severity: string) => {
      const map: { [key: string]: string } = {
        CRITICAL: 'Critique',
        HIGH: 'Élevée',
        MEDIUM: 'Moyenne',
        LOW: 'Faible',
        BLOCKING: 'Bloquant',
        HIGH_RISK: 'Risque élevé',
        WARNING: 'Avertissement',
        INFO: 'Information'
      }
      return map[severity] || severity
    }

    const getCheckResultIcon = (result: string) => {
      const icons: any = {
        'PASSED': 'fas fa-check-circle',
        'FAILED': 'fas fa-times-circle',
        'WARNING': 'fas fa-exclamation-triangle',
        'PENDING': 'fas fa-clock',
        'NOT_APPLICABLE': 'fas fa-minus-circle'
      }
      return icons[result] || 'fas fa-question-circle'
    }

    const getSeverityBadgeClass = (severity: string) => {
      const classes: any = {
        'CRITICAL': 'bg-danger',
        'HIGH': 'bg-warning text-dark',
        'MEDIUM': 'bg-info',
        'LOW': 'bg-light text-dark',
        'BLOCKING': 'bg-danger',
        'WARNING': 'bg-warning text-dark',
        'INFO': 'bg-info'
      }
      return classes[severity] || 'bg-secondary'
    }

    const getCheckStats = (checks: any[]) => {
      if (!checks || !checks.length) return null

      const passed = checks.filter(c => c.result === 'PASSED').length
      const failed = checks.filter(c => c.result === 'FAILED').length
      const warning = checks.filter(c => c.result === 'WARNING').length
      const pending = checks.filter(c => c.result === 'PENDING').length
      const notApplicable = checks.filter(c => c.result === 'NOT_APPLICABLE').length

      const completed = passed + failed + warning + notApplicable

      const stats = {
        total: checks.length,
        passed,
        failed,
        warning,
        pending,
        notApplicable,
        completion: Math.round((completed / checks.length) * 100)
      }

      return stats
    }




    const allTasksCompleted = computed(() =>
      validationTasks.value.every(task => task.status === 'COMPLETED')
    );

    const allCorrections = computed(() => {
      return validationTasks.value.flatMap(task =>
        task.checks
          ?.filter(check =>
            (check.result === 'FAILED' || check.result === 'WARNING')
          )
          .map(check => ({
            taskId: task.id,
            taskName: task.libelle,
            checkId: check.id,
            checkTitle: check.title,
            result: check.result,
            severity: check.severity,
            observation: check.comments || '',
            correctionNeeded: check.comments || '',
          })) || []
      );
    });

    const allCorrectionsHaveComments = computed(() => {
      return allCorrections.value.every(corr => corr.hasComment);
    });

    const correctionsWithoutComments = computed(() => {
      return allCorrections.value.filter(corr => !corr.hasComment);
    });

    const pendingCorrections = ref<any[]>([]);

    const loadPendingCorrections = async () => {
      try {
        const response = await ApiService.get(`/correctionR/besc/${bescId}`);
        pendingCorrections.value = response.data.data.filter((cor: any) =>
          cor.status !== 'RESOLVED' && cor.status !== 'Terminée'
        );
      } catch (err) {
        console.error('Erreur chargement corrections:', err);
      }
    };

    const globalProgressionStats = computed(() => {
      const total = getTotalChecksCount();
      const passed = validationTasks.value.reduce((sum, task) =>
        sum + (task.checks?.filter(c => c.result === 'PASSED').length || 0), 0
      );
      const warning = validationTasks.value.reduce((sum, task) =>
        sum + (task.checks?.filter(c => c.result === 'WARNING').length || 0), 0
      );
      const failed = validationTasks.value.reduce((sum, task) =>
        sum + (task.checks?.filter(c => c.result === 'FAILED').length || 0), 0
      );

      return {
        totalChecks: total,
        passedPercentage: total > 0 ? Math.round((passed / total) * 100) : 0,
        warningPercentage: total > 0 ? Math.round((warning / total) * 100) : 0,
        failedPercentage: total > 0 ? Math.round((failed / total) * 100) : 0,
        passedCount: passed,
        warningCount: warning,
        failedCount: failed
      };
    });



    const loadComments = async () => {
      try {
        const res = await ApiService.get(`/besc/${bescId}/comments`)
        console.log("commentaires:", res)
        comments.value = res.data.data || []
      } catch (err: any) {
        error('Erreur lors du chargement des commentaires')
      }
    }

    const statusBadgeClass = computed(() => {
      const code = besc.value.currentStatus?.code
      if (!code) return 'bg-secondary'

      if (code === 'SUBMITTED') {
        const activeFacture = besc.value.factures?.find(
          f => f.status !== 'CANCELLED' && f.status !== 'VOID'
        )

        if (!activeFacture) {
          return 'bg-info text-white'
        }

        if (activeFacture.status === 'PAID') {
          return 'bg-warning text-dark'
        }
        return 'bg-info text-white'
      }

      const map: any = {
        DRAFT: 'bg-secondary text-white',
        PENDING: 'bg-warning text-dark',
        VALIDATED: 'bg-success text-white',
        REJECTED: 'bg-danger text-white',
        CANCELLED: 'bg-dark text-white'
      }

      return map[code] || 'bg-secondary'
    })

    const statusLabel = computed(() => {
      const code = besc.value.currentStatus?.code

      if (code === 'SUBMITTED') {
        const activeFacture = besc.value.factures?.find(
          f => f.status !== 'CANCELLED' && f.status !== 'VOID'
        )

        if (activeFacture?.status === 'PAID') {
          return besc.value.currentStatus?.label
        }

        return 'Non payé'
      }

      return besc.value.currentStatus?.label || 'Inconnu'
    })

    const formatDate = (date: string) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR')
    }

    const formatDateTime = (date: string) => {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR')
    }

    const formatNumber = (val: any) => {
      if (val === null || val === undefined) return '-'
      return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(val)
    }

    // const getDocumentUrl = (docId: string) => {
    //   if (!bescId) return "#"; 
    //   return `${process.env.VUE_APP_API_URL}/besc/${bescId}/documents/${docId}/download`;
    // };


    const openDocument = async (bescId: string, documentId: string) => {
      try {
        if (!bescId || !documentId) {
          console.error("Paramètre manquant", { bescId, documentId });
          return;
        }
        const token = localStorage.getItem("id_token");
        if (!token) {
          console.error("Token manquant !");
          return;
        }

        const url = `/besc/${bescId}/documents/${documentId}/view`;
        console.log("URL appelée =", url);

        const response = await ApiService.vueInstance.axios.get(url, {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("Réponse reçue du backend :", response);

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        console.log("Blob créé :", blob);

        const fileURL = URL.createObjectURL(blob);
        console.log("URL du fichier créé :", fileURL);

        window.open(fileURL, "_blank");
        console.log("Fichier ouvert dans un nouvel onglet ");

      } catch (error) {
        console.error("Erreur lors de l'ouverture du document :", error);
      }
    };

    const openFlags = computed(() =>
      autoFlags.value.filter(f => f.status === 'OPEN')
    )

    const criticalFlags = computed(() =>
      openFlags.value.filter(f => f.is_blocking === true)
    )

    const blockingFlags = computed(() =>
      criticalFlags.value.filter(f => f.severity === 'BLOCKING')
    )

    const highRiskFlags = computed(() =>
      criticalFlags.value.filter(f => f.severity === 'HIGH_RISK')
    )

    const warningFlags = computed(() =>
      openFlags.value.filter(f => f.severity === 'WARNING')
    )

    const infoFlags = computed(() =>
      openFlags.value.filter(f => f.severity === 'INFO')
    )

    const totalCriticalFlags = computed(() => criticalFlags.value.length)
    const totalBlockingFlags = computed(() => blockingFlags.value.length)
    const totalHighRiskFlags = computed(() => highRiskFlags.value.length)
    const totalWarningFlags = computed(() => warningFlags.value.length)
    const totalInfoFlags = computed(() => infoFlags.value.length)
    const totalAllFlags = computed(() => openFlags.value.length)



    const fetchRetourReponse = async () => {
      try {
        const { data } = await ApiService.get(`/corrections/besc/${bescId}`)

        correctionRetourne.value = data.data || []

        correctionRetourne.value = correctionRetourne.value.filter((cor: any) => cor.status === 'RESOLVED');

        console.log("valll", correctionRetourne.value);

        console.log(`${correctionRetourne.value.length} retour(s) de correction récupéré(s)`)

      } catch (err: any) {
        console.error('Erreur récupération retours:', err)
        if (err.response?.status !== 404) {
          error('Impossible de récupérer les retours de correction')
        }
      }
    }

    const getTotalChecksCount = () => {
      return validationTasks.value.reduce((sum, task) =>
        sum + (task.checks?.length || 0), 0
      );
    };

    const getCompletedChecksCount = () => {
      return validationTasks.value.reduce((sum, task) =>
        sum + (task.checks?.filter(check =>
          check.result && check.result !== 'PENDING'
        ).length || 0), 0
      );
    };

    const globalCompletionPercentage = computed(() => {
      const totalChecks = getTotalChecksCount();
      const completedChecks = getCompletedChecksCount();

      return totalChecks > 0 ? Math.round((completedChecks / totalChecks) * 100) : 0;
    });

    const isDossierValidated = computed(() => {
      // if (totalCriticalFlags.value > 0) return false;

      if (!validationTasks.value.length) return false;

      const allTasksCompleted = validationTasks.value.every(task => {
        const allChecksCompleted = task.checks?.every(check =>
          check.result && check.result !== 'PENDING'
        ) || false;

        return task.status === 'COMPLETED' && allChecksCompleted;
      });

      return allTasksCompleted;
    });

    const isAlreadyValidatedByAgent = computed(() => {
      return validationTasks.value.some(task =>
        task.agent_decision === 'VALIDATED' || task.agent_decision === 'REJECTED'
      );
    });

    const canValidateDossier = computed(() => {
      return isDossierValidated.value &&
        isAgent.value &&
        !isAlreadyValidatedByAgent.value;
    });

    const getAgentValidationStatus = () => {
      const validatedTask = validationTasks.value.find(task => task.agent_decision);
      return validatedTask?.agent_decision;
    };

    const getAgentValidationDate = () => {
      const validatedTask = validationTasks.value.find(task => task.agent_validated_at);
      return validatedTask?.agent_validated_at;
    };

    const showConteneurs = (conteneurs: any[], blNumber: string) => {
      selectedConteneurs.value = conteneurs
      selectedBlNumber.value = blNumber
      showConteneursModal.value = true
    }

    const openCommentModal = (bl: any, blIndex: number) => {
      selectedBl.value = bl
      selectedBlIndex.value = blIndex

      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: 'BILL_OF_LADING',
        related_entity_id: bl.id,
        validation_task_id: null
      }

      showCommentFormModal.value = true
    }

    const openCommentModalWithTask = (
      entityType: string,
      entityId: string | null,
      taskId: string | null = null
    ) => {
      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: entityType,
        related_entity_id: entityId,
        validation_task_id: taskId
      }
      editingCommentId.value = null
      showCommentFormModal.value = true
    }

    // const currentUser = ref({ role: 'Administrateur' });
    // const getTaskComments = (taskId: string) => {
    //   let taskComments = comments.value.filter(c => c.validation_task_id === taskId);
    //   const userRole = currentUser.value?.role || 'Transitaire';
    //   if (userRole === 'Transitaire') {
    //     taskComments = taskComments.filter(c => c.visibility === 'EXTERNAL');
    //   }
    //   return taskComments;
    // }


    const getTaskComments = (taskId: string) => {

      console.log('Nombre total de commentaires pour cette tâche :',
        comments.value.filter(c => c.validation_task_id === taskId).length
      );
      console.log('Parmi eux, combien sont INTERNAL :',
        comments.value.filter(c => c.validation_task_id === taskId && c.visibility === 'INTERNAL').length
      );

      const allCommentsForTask = comments.value.filter(
        c => c.validation_task_id === taskId
      );
      console.log("adminnnnnnnnnn", isAdmin.value);
      if (isAdmin.value || isAgent.value) {
        console.log('Utilisateur autorisé → on affiche TOUT');
        return allCommentsForTask;
      }

      console.log(' Utilisateur non autorisé → on filtre seulement EXTERNAL');
      return allCommentsForTask.filter(c => c.visibility === 'EXTERNAL');
    };
    const getTaskEntityComments = (taskId: string, entityType: string, entityId: string) =>
      comments.value.filter(c =>
        c.validation_task_id === taskId &&
        c.related_entity_type === entityType &&
        c.related_entity_id === entityId
      )

    const openGlobalCommentModal = () => {
      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: 'BESC_FILE',
        related_entity_id: bescId,
        validation_task_id: null
      }
      editingCommentId.value = null
      showCommentFormModal.value = true
    }

    const openDocumentCommentModal = (documentId: string) => {
      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: 'SUPPORTING_DOCUMENT',
        related_entity_id: documentId,
        validation_task_id: null
      }
      editingCommentId.value = null
      showCommentFormModal.value = true
    }

    const editComment = (comment: any) => {
      commentForm.value = {
        visibility: comment.visibility,
        comment_text: comment.commentText,
        related_entity_type: comment.relatedEntityType,
        related_entity_id: comment.relatedEntityId,
        validation_task_id: comment.validationTaskId
      }
      editingCommentId.value = comment.id
      showCommentFormModal.value = true
    }

    const closeCommentModal = () => {
      showCommentModal.value = false
      selectedBl.value = null
      selectedBlIndex.value = -1
      currentComment.value = ''
    }


    const saveComment = async () => {
      try {
        if (!commentForm.value.comment_text.trim()) {
          error('Le commentaire ne peut pas être vide')
          return
        }


        const payload = {
          visibility: commentForm.value.visibility,
          commentText: commentForm.value.comment_text,
          relatedEntityType: commentForm.value.related_entity_type,
          relatedEntityId: commentForm.value.related_entity_id,
          validationTaskId: commentForm.value.validation_task_id
        }

        if (editingCommentId.value) {
          await ApiService.put(`/besc/${bescId}/comments/${editingCommentId.value}`, payload)
          success('Commentaire modifié avec succès !')
        } else {
          await ApiService.post(`/besc/${bescId}/comments`, payload)
          success('Commentaire ajouté avec succès !')
        }

        await loadComments()
        closeCommentFormModal()
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de l\'enregistrement du commentaire')
      }
    }

    const validateDossier = async (decision: 'VALIDATED' | 'REJECTED') => {
      validatingDossier.value = true;

      try {
        await ApiService.post(`/besc/${bescId}/agent-validation`, {
          decision: decision,
          agent_comment: validationComment.value
        });

        await loadValidationTasks();

        showValidationModal.value = false;
        validationComment.value = '';

        if (decision === 'VALIDATED') {
          success('Dossier validé avec succès ! Il est maintenant en attente d\'approbation superviseur.');
        } else {
          success('Dossier rejeté. Il sera remis en correction.');
        }

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de la validation du dossier');
      } finally {
        validatingDossier.value = false;
      }
    };

    const deleteComment = async (commentId: string) => {
      const confirmed = await monSwal(
        'Êtes-vous sûr de vouloir supprimer ce commentaire ?',
        'Supprimer',
        'btn btn-danger',
        'Annuler',
        'Suppression annulée'
      );

      if (!confirmed) return;

      try {
        await ApiService.delete(`/besc/${bescId}/comments/${commentId}`);
        await loadComments();
        success('Commentaire supprimé avec succès !');
      } catch (err: any) {
        error('Erreur lors de la suppression du commentaire');
      }
    };

    const closeCommentFormModal = () => {
      showCommentFormModal.value = false
      editingCommentId.value = null
      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: 'BESC_FILE',
        related_entity_id: null,
        validation_task_id: null
      }
    }

    const globalComments = computed(() =>
      comments.value.filter(c => c.relatedEntityType === 'BESC_FILE')
    )

    // const getBlComments = (blId: string) =>
    //   comments.value.filter(c =>
    //     c.relatedEntityType === 'BILL_OF_LADING' && c.relatedEntityId === blId
    //   )

    const getBlComments = (blId: string) => {
      let blComments = comments.value.filter(c =>
        c.relatedEntityType === 'BILL_OF_LADING' && c.relatedEntityId === blId
      );
      if (!(isAdmin.value || isAgent.value)) {
        blComments = blComments.filter(c => c.visibility === 'EXTERNAL');
      }

      return blComments;
    };

    const getDocumentComments = (docId: string) =>
      comments.value.filter(c =>
        c.relatedEntityType === 'SUPPORTING_DOCUMENT' && c.relatedEntityId === docId
      )

    const totalMarchandises = computed(() => {
      if (!besc.value.bls?.length) return 0
      return besc.value.bls.reduce((sum: number, bl: any) => {
        return sum + (bl.bescLignes?.length || 0)
      }, 0)
    })

    onActivated(() => {
      console.log(' Page activée - Rechargement des données')
      loadBesc()
      loadComments()
      loadValidationTasks()
    })

    watch(
      () => route.params.id,
      (newId) => {
        if (newId) {
          console.log(' Changement d\'ID détecté:', newId)
          loadBesc()
        }
      }
    )

    const loadBesc = async () => {
      try {
        loading.value = true
        console.log(' [VUE] Chargement BESC ID:', bescId)

        // Charger le BESC
        const bescRes = await ApiService.get(`/besc/${bescId}`)
        besc.value = bescRes.data.data

        console.log(' [VUE] BESC chargé:', {
          ref: besc.value.refbesc,
          currentStatusId: besc.value.currentStatusId,
          currentStatus: besc.value.currentStatus,
          nombreFactures: besc.value.factures?.length || 0
        })

        // Charger l'historique des statuts
        const historyRes = await ApiService.get(`/besc/${bescId}/status-history`)
        statusHistory.value = historyRes.data.data || []

        const flagsRes = await ApiService.get(`/besc/${bescId}/auto-flags`)
        autoFlags.value = Array.isArray(flagsRes.data) ? flagsRes.data : flagsRes.data.data || []

        bescActeurs.value = besc.value.bescActeurs || []

        console.log(' [VUE] Toutes les données chargées avec succès')
        console.log(' [VUE] Statut actuel:', {
          code: besc.value.currentStatus?.code,
          label: besc.value.currentStatus?.label
        })

      } catch (err: any) {
        console.error(' [VUE] Erreur chargement BESC:', err)
        error(err.response?.data?.message || 'Impossible de charger le BESC')
        router.push('/liste-besc')
      } finally {
        loading.value = false
      }
    }


    const importateur = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Importateur")
    )

    const transitaire = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Transitaire")
    )

    const expediteur = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Expediteur")
    )

    const transporteur = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Transporteur")
    )

    const partieANotifier = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Partie à notifier")
    )

    const transbordement = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Transporteur Après transbordement")
    )

    const printPDF = async () => {
      try {
        const response = await ApiService.getFile(`/besc/${bescId}/download-draft`)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      } catch (err) {
        error('Erreur lors du téléchargement du PDF')
      }
    }

    const goToEdit = () => {
      router.push({ name: 'EditAddBescStep1', params: { id: bescId } })
    }

    const goToEditStep2 = () => {
      router.push({ name: 'EditAddBescStep2', params: { id: bescId } })
    }


    onMounted(() => {
      loadBesc()
      loadAppliedRules()
      loadComments()
      loadValidationTasks()
      loadPendingCorrections()
      fetchRetourReponse()
    })

    return {
      besc,
      bescId,
      isAgent,
      isSuperviseur,
      isReverificationNeeded,
      validationCorrectionSchema,
      statusHistory,
      appliedRules,
      loadAppliedRules,
      loading,
      statusBadgeClass,
      formatDate,
      formatDateTime,
      formatNumber,
      // getDocumentUrl,
      openDocument,
      printPDF,
      goToEdit,
      validationtaskOptions,
      addCorrectionRequest,
      bescActeurs,
      importateur,
      transitaire,
      expediteur,
      transporteur,
      partieANotifier,
      transbordement,
      autoFlags,
      blockingFlags,
      highRiskFlags,
      warningFlags,
      showFlagsModal,
      infoFlags,
      openCorrectionRequestModal,
      totalCriticalFlags,
      totalBlockingFlags,
      totalHighRiskFlags,
      totalWarningFlags,
      totalInfoFlags,
      totalAllFlags,
      totalMarchandises,
      showConteneursModal,
      selectedConteneurs,
      selectedBlNumber,
      showConteneurs,
      showCommentModal,
      selectedBl,
      selectedBlIndex,
      currentComment,
      closeCommentModal,
      comments,
      selectedValidationTask,
      showCommentFormModal,
      commentForm,
      editingCommentId,
      openGlobalCommentModal,
      openDocumentCommentModal,
      openCommentModal,
      editComment,
      deleteComment,
      showCorrectionRequestModal,
      closeCommentFormModal,
      isAdmin,
      globalComments,
      getBlComments,
      getDocumentComments,
      saveComment,
      validationTasks,
      loadValidationTasks,
      openCommentModalWithTask,
      getTaskComments,
      getTaskEntityComments,
      loadingChecks,
      updateCheck,
      completeTask,
      getTaskProgress,
      getCheckResultClass,
      getCheckResultIcon,
      getSeverityBadgeClass,
      getCheckStats,
      translateStatus,
      translateSeverity,
      statusLabel,
      visibiliteOptions,
      resultatCheckOptions,
      taskOptions,
      getTotalChecksCount,
      getCompletedChecksCount,
      globalCompletionPercentage,
      isDossierValidated,
      isAlreadyValidatedByAgent,
      canValidateDossier,
      getAgentValidationStatus,
      getAgentValidationDate,
      showValidationModal,
      validatingDossier,
      validationComment,
      validateDossier,
      allTasksCompleted,
      allCorrections,
      globalProgressionStats,
      pendingCorrections,
      loadPendingCorrections,
      correctionRetourne,
      showDocumentModal,
      currentDocumentUrl,
      currentDocumentName,
      currentDocumentType,
      loadingDocument,
      openDocumentModal,
      closeDocumentModal
    }
  }
})
</script>
<style scoped>
.section-title h5 {
  position: relative;
  padding-left: 15px;
}

.section-title h5::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background-color: #0a59a4;
  border-radius: 2px;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #ddd;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-marker {
  position: absolute;
  left: -30px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #0a59a4;
  border: 3px solid white;
  box-shadow: 0 0 0 3px #0a59a4;
}

.timeline-content {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid #0a59a4;
}

.bg-blue-header {
  background-color: #0a59a4 !important;
}

/* Forcer le fond blanc pour les accordéons de tâches même quand ouvert */
.accordion-button:not(.collapsed) {
  background-color: #ffffff !important;
  color: #000000 !important;
}

.accordion-button:focus {
  box-shadow: none;
  border-color: rgba(0, 0, 0, .125);
}

.accordion-button:not(.collapsed) {
  background-color: #0a59a4 !important;
  color: white !important;
  box-shadow: none;
}

.accordion-button:focus {
  box-shadow: 0 0 0 0.25rem rgba(10, 89, 164, 0.25);
  border-color: #0a59a4;
}

.accordion-button::after {
  filter: brightness(0) invert(1);
}

.accordion-item {
  border-radius: 8px !important;
  overflow: hidden;
}

.accordion-button {
  font-size: 1.05rem;
  padding: 1rem 1.25rem;
}

.card.border-primary {
  border-width: 2px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.card.border-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(10, 89, 164, 0.15);
}

/* Ajouter à la fin de ta section <style scoped> existante */

/* Styles pour les checks */
.checks-list {
  max-height: 600px;
  overflow-y: auto;
}

.check-item {
  transition: all 0.2s ease;
  border-width: 2px;
}

.check-item:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.check-item.border-success {
  background: linear-gradient(to right, #f0fff4 0%, #ffffff 50%);
}

.check-item.border-danger {
  background: linear-gradient(to right, #fff5f5 0%, #ffffff 50%);
}

.check-item.border-warning {
  background: linear-gradient(to right, #fffbeb 0%, #ffffff 50%);
}

.check-item .form-select-sm,
.check-item .form-control-sm {
  font-size: 0.875rem;
}

.progress {
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.6s ease;
}

/* Animation au chargement */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.check-item {
  animation: slideIn 0.3s ease;
}

.small-multiselect :deep(.multiselect) {
  font-size: 0.875rem;
}

.small-multiselect :deep(.multiselect-wrapper) {
  min-height: 38px;
}

.small-multiselect :deep(.multiselect-single-label) {
  font-size: 0.875rem;
}

.check-item.automatic {
  background: linear-gradient(to right, #e7f3ff 0%, #ffffff 50%);
  border-left: 4px solid #0d6efd !important;
}

.check-item.automatic .badge.bg-info {
  animation: pulse-glow 2s infinite;
}

@keyframes pulse-glow {

  0%,
  100% {
    opacity: 1;
    box-shadow: 0 0 0 rgba(13, 110, 253, 0.4);
  }

  50% {
    opacity: 0.85;
    box-shadow: 0 0 8px rgba(13, 110, 253, 0.6);
  }
}

.check-item.automatic :deep(.multiselect.is-disabled),
.check-item.automatic textarea:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Styles pour les checks compacts en 2 colonnes */
.check-item-compact {
  transition: all 0.2s ease;
  border-width: 2px;
  border-left-width: 4px !important;
}

.check-item-compact:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.check-item-compact.border-success {
  background: linear-gradient(135deg, #f0fff4 0%, #ffffff 100%);
  border-left-color: #22c55e !important;
}

.check-item-compact.border-danger {
  background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);
  border-left-color: #ef4444 !important;
}

.check-item-compact.border-warning {
  background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);
  border-left-color: #f59e0b !important;
}

.check-item-compact.border-secondary {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-left-color: #6c757d !important;
}

.check-item-compact.automatic {
  background: linear-gradient(135deg, #e7f3ff 0%, #ffffff 100%);
  border-left-color: #0d6efd !important;
}

.check-item-compact.automatic .badge.bg-info {
  animation: pulse-glow 2s infinite;
}

/* Multiselect compact */
.small-multiselect :deep(.multiselect) {
  font-size: 0.875rem;
  min-height: 36px;
}

.small-multiselect :deep(.multiselect-wrapper) {
  min-height: 36px;
}

.small-multiselect :deep(.multiselect-single-label) {
  font-size: 0.875rem;
  padding: 5px 12px;
}

.small-multiselect :deep(.multiselect-caret) {
  margin-right: 8px;
}

/* Textarea compact */
.check-item-compact textarea.form-control-sm {
  font-size: 0.875rem;
  resize: vertical;
  min-height: 70px;
}

.check-item-compact textarea:disabled,
.check-item-compact :deep(.multiselect.is-disabled) {
  background-color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Animation pour les checks automatiques */
@keyframes pulse-glow {

  0%,
  100% {
    opacity: 1;
    box-shadow: 0 0 0 rgba(13, 110, 253, 0.4);
  }

  50% {
    opacity: 0.85;
    box-shadow: 0 0 8px rgba(13, 110, 253, 0.6);
  }
}

/* Badges plus compacts */
.check-item-compact .badge.small {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

/* Scrollbar stylisée pour la liste des checks */
.checks-list {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
}

.checks-list::-webkit-scrollbar {
  width: 6px;
}

.checks-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.checks-list::-webkit-scrollbar-thumb {
  background: #0a59a4;
  border-radius: 10px;
}

.checks-list::-webkit-scrollbar-thumb:hover {
  background: #084580;
}

/* Animation au chargement */
@keyframes slideInCheck {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.check-item-compact {
  animation: slideInCheck 0.3s ease;
}

/* Responsive - 1 colonne sur mobile */
@media (max-width: 991px) {
  .check-item-compact {
    margin-bottom: 1rem;
  }
}

.check-card {
  transition: all 0.3s ease;
}

.check-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15) !important;
}

.accordion-button:not(.collapsed) {
  box-shadow: none !important;
}

.progress-bar-animated {
  animation: progress-bar-stripes 1s linear infinite;
}

@keyframes progress-bar-stripes {
  0% {
    background-position: 1rem 0;
  }

  100% {
    background-position: 0 0;
  }
}

/* Améliorer l'affichage du multiselect */
.multiselect {
  min-height: 38px;
}

.multiselect.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Améliorer les badges */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
}

/* Style pour les cartes de checks */
.border-2 {
  border-width: 2px !important;
}

/* Animation pour les checks complétés */
.border-success.check-card {
  animation: pulse-success 2s ease-in-out;
}

@keyframes pulse-success {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(25, 135, 84, 0);
  }

  50% {
    box-shadow: 0 0 0 10px rgba(25, 135, 84, 0.2);
  }
}
</style>